import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { opportunitiesApi, analysisApi, documentsApi, vendorsApi, vendorEvaluationApi, solicitationAnalysisApi, api } from '../api/client';
import PrintableReport from '../components/PrintableReport';
import ApplicableRegulations from '../components/ApplicableRegulations';
import { useToast } from '../components/Toast';
import IntakeTab from './IntakeTab';
import AcquisitionTab from './AcquisitionTab';
import ContractAdminTab from './ContractAdminTab';
import CapturePlanTab from './pursuit/CapturePlanTab';
import ComplianceMatrixTab from './pursuit/ComplianceMatrixTab';
import ProposalResponseTab from './pursuit/ProposalResponseTab';
import SelfScoreTab from './pursuit/SelfScoreTab';
import AwardDebriefTab from './pursuit/AwardDebriefTab';
import StageGuide from '../components/StageGuide';
import { WORKFLOW_STAGES, STAGE_LABELS, STAGE_ICONS } from '../lib/stages';

const STAGES: string[] = [...WORKFLOW_STAGES];

// Maps each of the 8 canonical lifecycle stages onto the workspace's existing
// tabs / acquisition sub-tabs. The stepper uses this to navigate; the reverse
// (activeTab -> stage) is used to highlight the current stage as the user clicks.
const STAGE_TO_TAB: Record<string, string> = {
  intake: 'intake',
  requirements: 'intake',            // requirements captured within Intake (FAR 11)
  market_research: 'intake',         // market research documents live under Intake
  acquisition_strategy: 'acquisition', // Acquisition Path sub-tab
  solicitation: 'draft',             // Solicitation Development sub-tab
  evaluation: 'matches',             // Vendor Matching + Selection & Evaluation
  award: 'evaluate',                 // Selection & Evaluation (award decision)
  administration_closeout: 'contract_admin',
};
// Reverse: which stage does a given tab represent (for stepper highlighting).
const TAB_TO_STAGE: Record<string, string> = {
  overview: 'intake',
  intake: 'intake',
  acquisition: 'acquisition_strategy',
  draft: 'solicitation',
  matches: 'evaluation',
  evaluate: 'award',
  contract_admin: 'administration_closeout',
};

export default function OpportunityDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [opp, setOpp] = useState<any>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  // Inline document editor state (edit a document's text and re-render its file).
  const [editingDoc, setEditingDoc] = useState<any>(null);
  const [editingText, setEditingText] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const reloadDocuments = useCallback(async () => {
    if (!id) return;
    try {
      const docsData = await api.get<any>(`/documents?opportunity_id=${id}`);
      setDocuments(docsData.items || []);
    } catch { /* ignore */ }
  }, [id]);
  
  // Lifecycle state
  const [lifecycle, setLifecycle] = useState<any>(null);
  const [checklist, setChecklist] = useState<any>(null);
  const [showChecklist, setShowChecklist] = useState(false);

  // Administration state — awards drives the Contract Admin "award finalized" gate.
  const [awards, setAwards] = useState<any[]>([]);
  const [showAwardSearch, setShowAwardSearch] = useState(false);
  const [awardVendorSearch, setAwardVendorSearch] = useState('');
  const [awardVendorResults, setAwardVendorResults] = useState<any[]>([]);

  // Procurement Case state
  const [procCase, setProcCase] = useState<any>(null);
  const [relatedNotices, setRelatedNotices] = useState<any[]>([]);
  const [versions, setVersions] = useState<any[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showAmendForm, setShowAmendForm] = useState(false);
  const [showLinkCase, setShowLinkCase] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [matching, setMatching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docsExpanded, setDocsExpanded] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('solicitation');
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [orgOptions, setOrgOptions] = useState<Array<{ label: string; stack: string }>>([]);
  const [resyncing, setResyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Solicitation analysis state
  const [solicitationAnalysis, setSolicitationAnalysis] = useState<any>(null);
  const [solicitationLoading, setSolicitationLoading] = useState(false);

  // Evaluate tab state
  const [vendorEvalData, setVendorEvalData] = useState<Record<string, any>>({});
  const [vendorNotes, setVendorNotes] = useState<Record<string, string>>({});
  const [vendorUploading, setVendorUploading] = useState<Record<string, boolean>>({});
  const [vendorAnalyzing, setVendorAnalyzing] = useState<Record<string, boolean>>({});

  // Deep analysis state
  const [deepAnalysis, setDeepAnalysis] = useState<Record<string, any>>({});
  const [deepAnalyzing, setDeepAnalyzing] = useState<Record<string, boolean>>({});
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);

  // Print mode state
  const [printMode, setPrintMode] = useState(false);

  useEffect(() => {
    loadOpportunity();
  }, [id]);

  // Contracting-organization options for the Overview dropdown (drives regulations + checklist add-ons).
  useEffect(() => {
    opportunitiesApi.orgOptions()
      .then((r: any) => setOrgOptions(r?.org_options || []))
      .catch(() => setOrgOptions([]));
  }, []);

  const loadOpportunity = async () => {
    try {
      setLoading(true);
      const data = await opportunitiesApi.get(id!);
      const oppData = data.item || data;
      setOpp(oppData);
      
      // Load analysis if already completed
      if (oppData.analysis && oppData.analysis_status === 'completed') {
        try {
          const analysisData = typeof oppData.analysis === 'string' ? JSON.parse(oppData.analysis) : oppData.analysis;
          setAnalysis({ analysis_run_id: id, output: analysisData });
        } catch (e) {
          console.error('Failed to parse analysis:', e);
        }
      }
      
      // Load documents for this opportunity
      try {
        const docsData = await api.get<any>(`/documents?opportunity_id=${id}`);
        setDocuments(docsData.items || []);
      } catch (e) {
        console.error('Failed to load documents:', e);
      }

      // Load saved evaluation vendors
      if (oppData.evaluation_vendors && oppData.evaluation_vendors.length > 0) {
        setMatches(oppData.evaluation_vendors);
      }

      // Load existing solicitation analysis if available
      try {
        const solRes = await solicitationAnalysisApi.getLatest(id!);
        if (solRes.analysis) {
          setSolicitationAnalysis(solRes.analysis);
        }
      } catch (e) {
        // No analysis yet
      }

      // Load linked lifecycle record
      try {
        const lcRes = await api.get<any>('/lifecycle');
        const lcItems = lcRes.items || [];
        // Match by opportunity_id first, then by title as fallback
        let linkedLc = lcItems.find((lc: any) => lc.opportunity_id === id);
        if (!linkedLc) {
          linkedLc = lcItems.find((lc: any) => lc.title && oppData.title && lc.title === oppData.title);
        }
        if (linkedLc) {
          setLifecycle(linkedLc);
          const clRes = await api.get<any>(`/lifecycle/${linkedLc.id}/checklist`);
          setChecklist(clRes);
          // Load awards (needed to detect a finalized award for the Contract Admin stage)
          try {
            const awRes = await api.get<any>(`/awards?lifecycle_id=${linkedLc.id}`).catch(() => ({ items: [] }));
            setAwards(awRes.items || []);
          } catch (adminErr) { /* non-blocking */ }
        } else {
          // No lifecycle exists — create one automatically
          try {
            const newLc = await api.post<any>('/lifecycle', {
              opportunity_id: id,
              title: oppData.title || '',
              agency: oppData.agency || '',
              contract_number: oppData.notice_id || '',
              estimated_value: oppData.budget || '',
              contract_type: oppData.notice_type || '',
            });
            if (newLc.lifecycle_id) {
              const lcDetail = await api.get<any>(`/lifecycle/${newLc.lifecycle_id}`);
              setLifecycle(lcDetail.item || newLc.item);
              const clRes = await api.get<any>(`/lifecycle/${newLc.lifecycle_id}/checklist`);
              setChecklist(clRes);
            }
          } catch (lcCreateErr) {
            console.warn('Auto-create lifecycle failed:', lcCreateErr);
          }
        }
      } catch (e) {
        // Lifecycle not available
      }

      // Load procurement case and related notices
      try {
        if (oppData.procurement_case_id) {
          const casesRes = await api.get<any>('/procurement-cases');
          const found = (casesRes.items || []).find((c: any) => c.case_number === oppData.procurement_case_id || c.id === oppData.procurement_case_id);
          if (found) setProcCase(found);
        }
        // Load related notices
        const relRes = await api.get<any>(`/notices/${id}/related`).catch(() => ({ related: [] }));
        setRelatedNotices(relRes.related || []);
        // Load version history
        const verRes = await api.get<any>(`/notices/${id}/versions`).catch(() => ({ versions: [] }));
        setVersions(verRes.versions || []);
      } catch (e) {
        // Non-blocking
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    setError('');
    setMessage('');
    try {
      const res = await analysisApi.run(id!);
      setMessage(`✓ AI analysis complete. Requirements extracted — available in Draft and Matches tabs.`);
      // fti-simple returns analysis directly in the opportunity
      const analysisData = res.analysis || res;
      setAnalysis({ analysis_run_id: id, output: typeof analysisData === 'string' ? JSON.parse(analysisData) : analysisData });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const runSolicitationAnalysis = async () => {
    setSolicitationLoading(true);
    setError('');
    setMessage('');
    try {
      // Use the main analysis endpoint which extracts procurement data from docs
      const res = await analysisApi.run(id!);
      const analysisData = res.analysis || res.output || res;
      if (typeof analysisData === 'string') {
        try { setSolicitationAnalysis(JSON.parse(analysisData)); } catch { setSolicitationAnalysis({ summary: analysisData }); }
      } else {
        setSolicitationAnalysis(analysisData);
      }
      setMessage('✓ Solicitation analysis complete');
    } catch (e: any) {
      // Try the solicitation-specific endpoint as fallback
      try {
        const res2 = await solicitationAnalysisApi.run(id!);
        setSolicitationAnalysis(res2.analysis || res2);
        setMessage('✓ Solicitation analysis complete');
      } catch (e2: any) {
        setError('Analysis failed: ' + (e2.message || e.message));
      }
    } finally {
      setSolicitationLoading(false);
    }
  };

  const runMatching = async () => {
    // Matching works off the opportunity's own data (title, description, NAICS,
    // PSC, and any stored analysis). A prior analysis run enriches results but is
    // not required — the backend handles the no-analysis case.
    setMatching(true);
    setError('');
    setMessage('');
    try {
      const matchesData = await vendorsApi.triggerMatching(id!, analysis?.analysis_run_id);
      // Deduplicate by vendor_name (keep first occurrence = highest score since sorted by fit_score DESC)
      const seen = new Set<string>();
      const rawMatches = matchesData.matches || matchesData.items || [];
      const uniqueMatches = rawMatches
        .sort((a: any, b: any) => (b.fit_score || 0) - (a.fit_score || 0))
        .filter((m: any) => {
          const key = (m.vendor_name || '').trim().toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      setMatches(uniqueMatches);
      // Persist matched vendors to opportunity
      api.put(`/opportunities/${id}`, { evaluation_vendors: uniqueMatches }).catch(err => console.error('Failed to save eval vendors:', err));
      setMessage(`✓ Vendor matching complete. Found ${uniqueMatches.length} matching vendors.`);
      setActiveTab('matches');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setMatching(false);
    }
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');
    setMessage('');
    const uploaded: any[] = [];
    const failed: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const result = await documentsApi.upload(id!, file);
        uploaded.push(result);

        // Also upload to the linked Lifecycle contract files (non-blocking)
        try {
          const reader = new FileReader();
          reader.onload = async () => {
            const base64 = (reader.result as string).split(',')[1];
            await api.post<any>('/contract-files', {
              opportunity_id: id,
              filename: file.name,
              file_content: base64,
              file_type: file.name.split('.').pop()?.toLowerCase() || 'pdf',
              category: uploadCategory,
              stage: 'planning',
            });
          };
          reader.readAsDataURL(file);
        } catch (lcErr) {
          console.warn('Lifecycle file sync (non-blocking):', lcErr);
        }
      } catch (err: any) {
        failed.push(file.name);
      }
    }

    if (uploaded.length > 0) {
      setDocuments([...documents, ...uploaded]);
      setMessage(`✓ ${uploaded.length} document${uploaded.length > 1 ? 's' : ''} uploaded and queued for processing`);
    }
    if (failed.length > 0) {
      setError(`Failed to upload: ${failed.join(', ')}`);
    }
    e.target.value = '';
    setUploading(false);
  };

  // --- Evaluate tab functions ---
  const loadVendorEvaluation = async (vendorId: string) => {
    try {
      const data = await vendorEvaluationApi.get(id!, vendorId);
      setVendorEvalData(prev => ({ ...prev, [vendorId]: data }));
    } catch (e) {
      console.error('Failed to load vendor evaluation:', e);
    }
  };

  const handleVendorFileUpload = async (vendorId: string, vendorName: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setVendorUploading(prev => ({ ...prev, [vendorId]: true }));
    setError('');
    try {
      await vendorEvaluationApi.upload(id!, vendorId, file, vendorNotes[vendorId] || '');
      setMessage(`✓ File "${file.name}" uploaded for ${vendorName}`);
      e.target.value = '';
      await loadVendorEvaluation(vendorId);
    } catch (err: any) {
      setError(`Upload failed for ${vendorName}: ${err.message}`);
    } finally {
      setVendorUploading(prev => ({ ...prev, [vendorId]: false }));
    }
  };

  const handleVendorNotesSubmit = async (vendorId: string, vendorName: string) => {
    const notes = vendorNotes[vendorId];
    if (!notes?.trim()) return;

    setVendorUploading(prev => ({ ...prev, [vendorId]: true }));
    setError('');
    try {
      await vendorEvaluationApi.addNotes(id!, vendorId, notes);
      setMessage(`✓ Notes saved for ${vendorName}`);
      setVendorNotes(prev => ({ ...prev, [vendorId]: '' }));
      await loadVendorEvaluation(vendorId);
    } catch (err: any) {
      setError(`Failed to save notes for ${vendorName}: ${err.message}`);
    } finally {
      setVendorUploading(prev => ({ ...prev, [vendorId]: false }));
    }
  };

  const handleVendorAnalyze = async (vendorId: string, vendorName: string) => {
    setVendorAnalyzing(prev => ({ ...prev, [vendorId]: true }));
    setError('');
    setMessage('');
    try {
      const result = await vendorEvaluationApi.analyze(id!, vendorId, vendorNotes[vendorId] || '');
      setMessage(`✓ AI evaluation complete for ${vendorName}`);
      const evalOutput = result.evaluation || result.evaluations?.[0] || result;
      setVendorEvalData(prev => ({
        ...prev,
        [vendorId]: {
          ...prev[vendorId],
          evaluation: {
            output: evalOutput,
            recommendation: evalOutput?.recommendation,
            confidence_score: evalOutput?.confidence_score,
          }
        }
      }));
    } catch (err: any) {
      setError(`Analysis failed for ${vendorName}: ${err.message}`);
    } finally {
      setVendorAnalyzing(prev => ({ ...prev, [vendorId]: false }));
    }
  };

  const handleDeleteVendorDoc = async (vendorId: string, documentId: string) => {
    try {
      await vendorEvaluationApi.deleteDocument(documentId);
      await loadVendorEvaluation(vendorId);
      setMessage('✓ Document removed');
    } catch (err: any) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const runDeepAnalysis = async (vendorId: string, vendorName: string) => {
    setDeepAnalyzing(prev => ({ ...prev, [vendorId]: true }));
    setError('');
    setMessage('');
    try {
      const result = await opportunitiesApi.deepAnalysis(id!, vendorId);
      setDeepAnalysis(prev => ({ ...prev, [vendorId]: result.analysis }));
      setExpandedVendor(vendorId);
      setMessage(`✓ Deep analysis complete for ${vendorName}`);
    } catch (err: any) {
      setError(`Deep analysis failed for ${vendorName}: ${err.message}`);
    } finally {
      setDeepAnalyzing(prev => ({ ...prev, [vendorId]: false }));
    }
  };

  if (loading) return <div role="status" aria-live="polite" style={{ color: '#6b7280', padding: 32 }}>Loading...</div>;
  if (error && !message && !opp) return <div role="alert" style={{ color: '#dc2626', padding: 32 }}>{error}</div>;
  if (!opp) return <div style={{ color: '#6b7280', padding: 32 }}>Not found</div>;

  // Render printable report view
  if (printMode) {
    return (
      <PrintableReport
        opportunity={opp}
        analysis={analysis}
        solicitationAnalysis={solicitationAnalysis}
        matches={matches}
        onClose={() => setPrintMode(false)}
      />
    );
  }

  // Draft/Matches/Evaluate now live INSIDE the Acquisition workspace as sub-tabs.
  const acqTabIds = ['acquisition', 'draft', 'matches', 'evaluate'];
  // Contract Administration & Closeout is the post-award stage of the workspace.
  // It becomes "active" when the award is finalized, but is always visible so the
  // team can reach it directly.
  const awardFinalized = opp?.status === 'awarded' || (awards && awards.length > 0) || !!opp?.contract_admin;

  // The furthest stage this opportunity has reached (for the stepper's completed
  // marks). Derived from the lifecycle/opp stage, with award status as a hard signal.
  const reachedStage = (() => {
    let s = lifecycle?.current_stage || (opp as any)?.derived_stage || (opp as any)?.current_stage || 'intake';
    if (awardFinalized) s = 'administration_closeout';
    return STAGES.includes(s) ? s : 'intake';
  })();
  const reachedIdx = STAGES.indexOf(reachedStage);
  // The stage the user is currently viewing (from the active tab/sub-tab).
  const activeStage = TAB_TO_STAGE[activeTab] || 'intake';
  const activeStageIdx = STAGES.indexOf(activeStage);

  // Buyside lens: a SELLER pursuit must NOT show the government buyer-authoring
  // lifecycle (Intake "describe agency needs", Acquisition/Solicitation Development,
  // Selection & Evaluation, Contract Admin). Sellers respond to solicitations; they
  // don't author them. Detected from the opportunity's pursuit_side (set on create),
  // so it's correct regardless of who is viewing. Buyer flow is unchanged.
  const isSellerPursuit = (opp?.pursuit_side || 'buyer') === 'seller';

  const buyerTabs = [
    { id: 'overview', label: '📋 Overview' },
    { id: 'intake', label: '📥 Intake' },
    { id: 'acquisition', label: '⚖️ Acquisition' },
    { id: 'contract_admin', label: awardFinalized ? '📑 Contract Admin & Closeout' : '📑 Contract Admin & Closeout (post-award)' },
  ];
  // Seller pursuit workspace (Phase 2 sub-phase 1): the vendor's capture/proposal
  // lifecycle. Qualify (Bid/No-Bid) + Teaming are dedicated app pages; the tabs here
  // are the in-workspace planning surfaces.
  const sellerTabs = [
    { id: 'overview', label: '📋 Overview' },
    { id: 'capture_plan', label: '🎯 Capture Plan' },
    { id: 'compliance_matrix', label: '✅ Compliance Matrix' },
    { id: 'proposal_response', label: '📝 Proposal Response' },
    { id: 'self_score', label: '⚖️ Self-Score' },
    { id: 'award', label: '🏆 Award & Debrief' },
  ];
  const tabs = isSellerPursuit ? sellerTabs : buyerTabs;

  // Guard: if a seller pursuit somehow lands on a buyer-only tab (deep link, stale
  // state), snap back to Overview so buyer-authoring surfaces never render.
  const sellerTabIds = ['overview', 'capture_plan', 'compliance_matrix', 'proposal_response', 'self_score', 'award'];
  const allowedTabIds = isSellerPursuit ? sellerTabIds : ['overview', 'intake', 'contract_admin', ...acqTabIds];
  if (opp && !allowedTabIds.includes(activeTab)) {
    // Defer to avoid setState-during-render warnings.
    setTimeout(() => setActiveTab('overview'), 0);
  }

  // Agency dropdown — the selected agency authoritatively determines the FAR supplement,
  // so it writes both `agency` and `contracting_organization` on save.
  const agencyField = (value: any) => {
    const dirty = pendingChanges.agency !== undefined;
    const current = dirty ? pendingChanges.agency : (value || '');
    const stack = (orgOptions.find(o => o.label === current) || {}).stack;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Agency</div>
        <select
          value={current}
          onChange={(e) => {
            const newVal = e.target.value;
            if (newVal !== (value || '')) {
              setPendingChanges(prev => ({ ...prev, agency: newVal, contracting_organization: newVal }));
            } else {
              setPendingChanges(prev => { const n = { ...prev }; delete n.agency; delete n.contracting_organization; return n; });
            }
          }}
          style={{ fontSize: 14, marginTop: 2, border: 'none', borderBottom: `1px solid ${dirty ? '#7c3aed' : '#e5e7eb'}`, padding: '2px 0', width: '100%', background: dirty ? '#faf5ff' : 'transparent', color: '#1f2937' }}
        >
          <option value="">Select agency…</option>
          {/* Preserve a legacy free-text agency that isn't in the curated list. */}
          {current && !orgOptions.some(o => o.label === current) && <option value={current}>{current} (current)</option>}
          {orgOptions.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
        </select>
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>
          {stack || 'Determines the FAR supplement, checklist add-ons, and clause scoping. Save to re-tailor.'}
        </div>
        {lifecycle?.id && (
          <button
            type="button"
            onClick={() => resyncChecklist(false)}
            disabled={resyncing || dirty}
            title={dirty ? 'Save the agency change first, then re-sync' : 'Add any missing agency-specific checklist items to the lifecycle (keeps completed items)'}
            style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: '#5b21b6', background: '#f5f3ff',
              border: '1px solid #c4b5fd', borderRadius: 6, padding: '4px 10px', cursor: (resyncing || dirty) ? 'not-allowed' : 'pointer', opacity: (resyncing || dirty) ? 0.6 : 1 }}
          >
            {resyncing ? 'Re-syncing…' : '↻ Re-sync checklist to agency'}
          </button>
        )}
      </div>
    );
  };

  const field = (label: string, value: any, fieldKey?: string) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      {fieldKey ? (
        <input
          defaultValue={value || ''}
          placeholder="—"
          onInput={(e: any) => {
            const newVal = e.target.value;
            if (newVal !== (value || '')) {
              setPendingChanges(prev => ({ ...prev, [fieldKey]: newVal }));
            } else {
              setPendingChanges(prev => { const n = { ...prev }; delete n[fieldKey]; return n; });
            }
          }}
          style={{ fontSize: 14, marginTop: 2, border: 'none', borderBottom: `1px solid ${pendingChanges[fieldKey] !== undefined ? '#7c3aed' : '#e5e7eb'}`, padding: '2px 0', width: '100%', background: pendingChanges[fieldKey] !== undefined ? '#faf5ff' : 'transparent', color: '#1f2937' }}
        />
      ) : (
        <div style={{ fontSize: 14, marginTop: 2, color: value ? '#1f2937' : '#d1d5db' }}>{value || '—'}</div>
      )}
    </div>
  );

  // Multiline editable field (for Description / Notes) — same pending-change behavior.
  const fieldArea = (label: string, value: any, fieldKey: string) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <textarea
        defaultValue={value || ''}
        placeholder="—"
        onInput={(e: any) => {
          const newVal = e.target.value;
          if (newVal !== (value || '')) {
            setPendingChanges(prev => ({ ...prev, [fieldKey]: newVal }));
          } else {
            setPendingChanges(prev => { const n = { ...prev }; delete n[fieldKey]; return n; });
          }
        }}
        style={{ fontSize: 13, lineHeight: 1.6, width: '100%', minHeight: 90, resize: 'vertical', boxSizing: 'border-box',
          border: `1px solid ${pendingChanges[fieldKey] !== undefined ? '#7c3aed' : '#e5e7eb'}`, borderRadius: 6, padding: '8px 10px',
          background: pendingChanges[fieldKey] !== undefined ? '#faf5ff' : 'white', color: '#374151' }}
      />
    </div>
  );

  const handleSaveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    const agencyChanged = pendingChanges.agency !== undefined || pendingChanges.contracting_organization !== undefined;
    setSaving(true);
    try {
      await opportunitiesApi.update(id!, pendingChanges);
      setOpp({ ...opp, ...pendingChanges });
      setPendingChanges({});
      setMessage('✓ Changes saved');
      // When the agency/org changed, re-tailor the linked lifecycle checklist to the
      // new supplement (additive; preserves completed items).
      if (agencyChanged && lifecycle?.id) {
        await resyncChecklist(true);
      }
    } catch (err: any) {
      setError('Save failed: ' + (err.message || 'Unknown error'));
    }
    setSaving(false);
  };

  // Append any missing agency-supplement add-on items to the linked lifecycle checklist.
  // Idempotent and additive — it never removes completed or existing items.
  const resyncChecklist = async (silent = false) => {
    if (!lifecycle?.id) return;
    setResyncing(true);
    try {
      const r = await api.post<any>(`/lifecycle/${lifecycle.id}/sync-agency-items`, {});
      // Reload the checklist so the new add-ons show immediately.
      try {
        const clRes = await api.get<any>(`/lifecycle/${lifecycle.id}/checklist`);
        setChecklist(clRes);
      } catch { /* non-fatal */ }
      const added = r?.items_added ?? 0;
      setMessage(added > 0 ? `✓ Checklist re-tailored — ${added} agency item(s) added` : '✓ Checklist already up to date for this agency');
    } catch (err: any) {
      if (!silent) setError('Re-sync failed: ' + (err.message || 'Unknown error'));
    }
    setResyncing(false);
  };

  return (
    <div style={{ maxWidth: 1200 }}>
      <Link to="/opportunities" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>← Opportunities</Link>
      
      {/* SAM-style header block */}
      <div style={{ margin: '12px 0 16px', borderBottom: '1px solid #e5e7eb', paddingBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2 style={{ fontSize: 22, lineHeight: 1.3, margin: 0, color: '#1f2937' }}>{opp.title}</h2>
              <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                background: opp.status === 'active' ? '#d1fae5' : opp.status === 'draft' ? '#fef3c7' : '#f3f4f6',
                color: opp.status === 'active' ? '#065f46' : opp.status === 'draft' ? '#92400e' : '#6b7280',
              }}>● {opp.status || 'Active'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {lifecycle && (
              <span style={{ padding: '8px 14px', background: '#d1fae5', color: '#065f46', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                ● Active Case — {STAGE_LABELS[lifecycle.current_stage] || lifecycle.current_stage}
              </span>
            )}
            <button onClick={() => setPrintMode(true)} style={{ padding: '8px 14px', background: '#374151', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🖨️ Print / PDF</button>
            {Object.keys(pendingChanges).length > 0 && (
              <button onClick={handleSaveChanges} disabled={saving} style={{ padding: '8px 14px', background: saving ? '#93c5fd' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
                {saving ? '⏳ Saving...' : `💾 Save (${Object.keys(pendingChanges).length})`}
              </button>
            )}
          </div>
        </div>

        {/* Key data points grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0, marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <div style={{ borderRight: '1px solid #e5e7eb', paddingRight: 16 }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Procurement Case</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#4338ca', marginTop: 2 }}>{opp.procurement_case_id || '(pending)'}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>Related Notice</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 }}>{opp.related_notice_id || opp.related_notice || '(blank)'}</div>
          </div>
          <div style={{ borderRight: '1px solid #e5e7eb', padding: '0 16px' }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Contract Opportunity Type</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 }}>{opp.notice_type || opp.contract_opportunity_type || '(blank)'}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>Contract Line Item Number</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 }}>{opp.contract_line_item_number || '(blank)'}</div>
          </div>
          <div style={{ borderRight: '1px solid #e5e7eb', padding: '0 16px' }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Inactive Date</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 }}>{opp.inactive_date || '(blank)'}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>Inactive Policy</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 }}>{opp.inactive_policy || '(blank)'}</div>
          </div>
          <div style={{ paddingLeft: 16 }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Date Offers Due</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', marginTop: 2 }}>{opp.response_deadline || opp.date_offers_due || '(blank)'}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>Published Date</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 }}>{opp.publication_date || opp.published_date || opp.posted_date || '(blank)'}</div>
          </div>
        </div>
      </div>

      {message && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#166534' }}>
          {message}
        </div>
      )}
      {error && !opp && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          {error}
        </div>
      )}

      {/* ═══ LIFECYCLE STAGE STEPPER — guided 8-stage progress + navigation ═══ */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 12px', marginBottom: 16, overflowX: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 'min-content' }}>
          {STAGES.map((stage, i) => {
            const isCurrent = i === activeStageIdx;
            const isComplete = i < reachedIdx && !isCurrent;
            const isReachable = true; // all stages navigable; content itself guides the user
            const dotBg = isCurrent ? '#4f46e5' : isComplete ? '#10b981' : '#e5e7eb';
            const dotColor = isCurrent || isComplete ? 'white' : '#9ca3af';
            const labelColor = isCurrent ? '#4f46e5' : isComplete ? '#065f46' : '#9ca3af';
            return (
              <React.Fragment key={stage}>
                <button
                  onClick={() => setActiveTab(STAGE_TO_TAB[stage] as any)}
                  title={`${STAGE_LABELS[stage]}${isComplete ? ' (reached)' : isCurrent ? ' (current)' : ''}`}
                  aria-current={isCurrent ? 'step' : undefined}
                  style={{
                    flex: '1 1 0', minWidth: 82, display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 6, background: 'none', border: 'none', cursor: isReachable ? 'pointer' : 'default', padding: 0,
                  }}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%', background: dotBg, color: dotColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
                    fontWeight: 700, boxShadow: isCurrent ? '0 0 0 4px rgba(79,70,229,0.15)' : 'none',
                    transition: 'all 0.15s',
                  }}>
                    {isComplete ? '✓' : STAGE_ICONS[stage]}
                  </div>
                  <div style={{ fontSize: 10.5, fontWeight: isCurrent ? 700 : 500, color: labelColor, textAlign: 'center', lineHeight: 1.2, maxWidth: 90 }}>
                    {STAGE_LABELS[stage]}
                  </div>
                </button>
                {i < STAGES.length - 1 && (
                  <div style={{ flex: '0 0 12px', height: 2, background: i < reachedIdx ? '#10b981' : '#e5e7eb', marginTop: 16, alignSelf: 'flex-start' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Documents Panel — collapsible, always accessible */}
      <div style={{ marginBottom: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', overflow: 'hidden' }}>
        <div
          onClick={() => setDocsExpanded(!docsExpanded)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', cursor: 'pointer', background: docsExpanded ? '#f9fafb' : 'white' }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
            📁 Documents {documents.length > 0 && <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginLeft: 6 }}>{documents.length}</span>}
          </span>
          <span style={{ fontSize: 11, color: '#6b7280' }}>{docsExpanded ? '▼ Collapse' : '▶ Expand'}</span>
        </div>
        {docsExpanded && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
            {/* Upload row */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <select
                value={uploadCategory}
                onChange={e => setUploadCategory(e.target.value)}
                style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
              >
                <option value="solicitation">Solicitation Package</option>
                <option value="sources_sought">Sources Sought / RFI</option>
                <option value="sow_pws">SOW / PWS</option>
                <option value="market_research">Market Research</option>
                <option value="vendor_proposal">Vendor Proposal</option>
                <option value="vendor_capability">Vendor Capability Statement</option>
                <option value="evaluation">Evaluation Document</option>
                <option value="award">Award Document</option>
                <option value="contract">Contract / Modification</option>
                <option value="invoice">Invoice / Receipt</option>
                <option value="correspondence">Correspondence</option>
                <option value="justification">Justification (J&A / Brand Name)</option>
                <option value="strategy">Acquisition Strategy</option>
                <option value="internal">Internal / Notes</option>
                <option value="other">Other</option>
              </select>
              <label style={{ padding: '7px 14px', background: '#4f46e5', color: 'white', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {uploading ? '⏳ Uploading...' : '+ Upload'}
                <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv,.txt,.png,.jpg" onChange={handleDocumentUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
              {documents.length > 0 && (
                <button
                  onClick={runAnalysis}
                  disabled={analyzing}
                  style={{ padding: '7px 14px', background: analyzing ? '#93c5fd' : '#7c3aed', color: 'white', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: analyzing ? 'wait' : 'pointer' }}
                >
                  {analyzing ? '⏳ Analyzing...' : '✨ AI Analyze'}
                </button>
              )}
            </div>
            {/* File list */}
            {documents.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0' }}>No documents uploaded yet. Select a category and upload files.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
                {documents.map((doc: any, idx: number) => (
                  <div key={doc.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#f9fafb', borderRadius: 4, fontSize: 12 }}>
                    <span style={{ color: '#6b7280' }}>📄</span>
                    <span style={{ flex: 1, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.filename || doc.original_filename || 'file'}</span>
                    {doc.source === 'generated' && (
                      <span style={{ padding: '2px 6px', background: '#f5f3ff', color: '#6d28d9', borderRadius: 4, fontSize: 10, whiteSpace: 'nowrap' }}>AI-generated</span>
                    )}
                    <span style={{ padding: '2px 6px', background: '#e0e7ff', color: '#4338ca', borderRadius: 4, fontSize: 10, whiteSpace: 'nowrap' }}>{doc.category || doc.document_type || 'other'}</span>
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{doc.file_size ? `${(doc.file_size / 1024).toFixed(0)}KB` : ''}</span>
                    <button
                      onClick={async () => {
                        try {
                          const res = await documentsApi.downloadUrl(doc.id);
                          if (res?.download_url) window.open(res.download_url, '_blank');
                        } catch (e) { /* ignore */ }
                      }}
                      title="Download"
                      style={{ padding: '2px 8px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      ⬇ Download
                    </button>
                    {/* Edit — only for AI-generated / text documents (has editable text). */}
                    {doc.source === 'generated' && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await documentsApi.get(doc.id);
                            setEditingDoc(doc);
                            setEditingText(res?.document?.extracted_text || '');
                          } catch { setEditingDoc(doc); setEditingText(''); }
                        }}
                        title="Edit content"
                        style={{ padding: '2px 8px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        ✏ Edit
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete ${doc.filename || 'this document'}? This cannot be undone.`)) return;
                        try {
                          await documentsApi.remove(doc.id);
                          await reloadDocuments();
                          toast('Document deleted.', 'success');
                        } catch (e: any) {
                          toast(e?.message || 'Could not delete (delete authority may be required).', 'error');
                        }
                      }}
                      title="Delete"
                      style={{ padding: '2px 8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      ✕ Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document editor modal — edit a generated document's text and re-render its file. */}
      {editingDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 10, width: 'min(860px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Edit Document</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{editingDoc.filename} — saving re-renders the file in its current format</div>
              </div>
              <button onClick={() => { setEditingDoc(null); setEditingText(''); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            <textarea
              value={editingText}
              onChange={e => setEditingText(e.target.value)}
              style={{ flex: 1, minHeight: 360, margin: 16, padding: 12, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', lineHeight: 1.6, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={() => { setEditingDoc(null); setEditingText(''); }} disabled={editBusy}
                style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={async () => {
                  if (!editingText.trim()) return;
                  setEditBusy(true);
                  try {
                    await documentsApi.updateText(editingDoc.id, editingText);
                    await reloadDocuments();
                    setEditingDoc(null); setEditingText('');
                    toast('Document updated.', 'success');
                  } catch (e: any) {
                    toast(e?.message || 'Could not save changes.', 'error');
                  }
                  setEditBusy(false);
                }}
                disabled={editBusy || !editingText.trim()}
                style={{ padding: '8px 16px', background: editBusy ? '#c4b5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: editBusy ? 'default' : 'pointer' }}
              >
                {editBusy ? 'Saving…' : '💾 Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {tabs.map(tab => {
          if ((tab as any).disabled) return <span key={tab.id} style={{ padding: '12px 4px', color: '#d1d5db', fontSize: 14, userSelect: 'none' }}>|</span>;
          const isLifecycleTab = tab.id.startsWith('lc_');
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '12px 14px',
                background: (activeTab === tab.id || (tab.id === 'acquisition' && acqTabIds.includes(activeTab))) ? 'white' : 'transparent',
                color: (activeTab === tab.id || (tab.id === 'acquisition' && acqTabIds.includes(activeTab))) ? (isLifecycleTab ? '#4f46e5' : '#1f2937') : '#6b7280',
                border: 'none',
                borderBottom: (activeTab === tab.id || (tab.id === 'acquisition' && acqTabIds.includes(activeTab))) ? `2px solid ${isLifecycleTab ? '#4f46e5' : '#2563eb'}` : 'none',
                fontSize: 13,
                fontWeight: activeTab === tab.id ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ACQUISITION SUB-TAB BAR (Solicitation & Acquisition Method) */}
      {acqTabIds.includes(activeTab) && (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '8px 4px 0', borderBottom: '1px solid #e5e7eb', marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { id: 'acquisition', label: '1. Acquisition Path' },
            { id: 'draft', label: '2. Solicitation Development' },
            { id: 'matches', label: '3. Vendor Matching' },
            { id: 'evaluate', label: '4. Selection & Evaluation' },
          ].map(st => (
            <button key={st.id} onClick={() => setActiveTab(st.id as any)} style={{
              padding: '8px 14px', border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer',
              fontSize: 12, fontWeight: activeTab === st.id ? 700 : 500,
              background: activeTab === st.id ? '#ede9fe' : 'transparent',
              color: activeTab === st.id ? '#5b21b6' : '#6b7280',
              borderBottom: activeTab === st.id ? '2px solid #7c3aed' : '2px solid transparent',
            }}>{st.label}</button>
          ))}
          <div style={{ flex: 1 }} />
          {(() => {
            let acq: any = {}; try { acq = opp.acquisition_data ? (typeof opp.acquisition_data === 'string' ? JSON.parse(opp.acquisition_data) : opp.acquisition_data) : {}; } catch { acq = {}; }
            const badges: string[] = [];
            if (acq.is_commercial === 'Yes' || (acq.commercial_classification && !['Noncommercial product or service', 'Undetermined', ''].includes(acq.commercial_classification))) badges.push('FAR 12');
            const m = acq.co_selected_method || acq.recommended_method || '';
            if (m.includes('13')) badges.push('FAR 13');
            if (m.includes('14')) badges.push('FAR 14');
            if (m.includes('15')) badges.push('FAR 15');
            return badges.map(b => (
              <span key={b} style={{ padding: '3px 10px', background: '#dcfce7', color: '#166534', borderRadius: 9999, fontSize: 11, fontWeight: 600, marginLeft: 6 }}>{b} Applicable</span>
            ));
          })()}
        </div>
      )}

      {/* Contextual "what to do here" guidance for the active stage */}
      <StageGuide id={activeTab} />

      {/* SELLER PURSUIT: Capture Plan */}
      {activeTab === 'capture_plan' && isSellerPursuit && (
        <CapturePlanTab oppId={id!} />
      )}

      {/* SELLER PURSUIT: Compliance Matrix */}
      {activeTab === 'compliance_matrix' && isSellerPursuit && (
        <ComplianceMatrixTab oppId={id!} />
      )}

      {/* SELLER PURSUIT: Proposal Response */}
      {activeTab === 'proposal_response' && isSellerPursuit && (
        <ProposalResponseTab oppId={id!} opp={opp} />
      )}

      {/* SELLER PURSUIT: Self-Score */}
      {activeTab === 'self_score' && isSellerPursuit && (
        <SelfScoreTab oppId={id!} />
      )}

      {/* SELLER PURSUIT: Award & Debrief */}
      {activeTab === 'award' && isSellerPursuit && (
        <AwardDebriefTab oppId={id!} />
      )}

      {/* ACQUISITION PATH (Tab 1) */}
      {activeTab === 'acquisition' && (
        <div>
          <AcquisitionTab opp={opp} oppId={id!} onSaved={(acq) => setOpp({ ...opp, acquisition_data: JSON.stringify(acq) })} goToSubTab={(t) => setActiveTab(t as any)} />
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
            <DraftGeneratorTab opp={opp} oppId={id!} analysis={analysis} documents={documents}
              onSaved={(drafts) => setOpp({ ...opp, solicitation_drafts: JSON.stringify(drafts) })}
              stages={['acquisition_strategy']} heading="Acquisition Strategy Documents" />
          </div>
        </div>
      )}

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div>
          {/* Seller pursuit: point to the seller tools; the buyer-authoring tabs are hidden. */}
          {isSellerPursuit && (
            <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)', border: '1px solid #c7d2fe', borderRadius: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#3730a3' }}>Seller pursuit</div>
              <div style={{ fontSize: 12, color: '#4338ca', margin: '4px 0 10px' }}>
                This is a pursuit, not a government acquisition. Use the seller tools to decide whether to bid and to line up partners.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/bid-no-bid')} style={{ padding: '7px 14px', background: '#4338ca', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Bid / No-Bid analysis →</button>
                <button onClick={() => navigate('/teaming')} style={{ padding: '7px 14px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Find teaming partners →</button>
              </div>
            </div>
          )}
          {/* Notice Context Banner - only show when a related active solicitation actually exists */}
          {relatedNotices.length > 0 && opp.notice_type && (opp.notice_type.toLowerCase().includes('rfi') || opp.notice_type.toLowerCase().includes('sources')) && 
           relatedNotices.some((rn: any) => rn.status === 'active' && ((rn.notice_type || '').toLowerCase().includes('solicitation') || (rn.notice_type || '').toLowerCase().includes('rfq') || (rn.notice_type || '').toLowerCase().includes('rfp'))) && (
            <div style={{ padding: '12px 16px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
              <div style={{ color: '#9a3412', fontWeight: 600 }}>You are viewing an earlier market-research notice. A related active solicitation may be available.</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button style={{ padding: '4px 10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Open Active Solicitation</button>
                <button style={{ padding: '4px 10px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Compare RFI with Solicitation</button>
                <button style={{ padding: '4px 10px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>View Procurement Lifecycle</button>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            {/* Notice Identity */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 13, marginBottom: 14, color: '#374151', fontWeight: 600 }}>Notice Identity</h3>
              {field('Notice Type', opp.notice_type || opp.contract_opportunity_type, 'notice_type')}
              {field('Title', opp.title, 'title')}
              {field('Sources Sought Notice ID', opp.sources_sought_notice_id, 'sources_sought_notice_id')}
              {field('Solicitation Notice ID', opp.solicitation_notice_id, 'solicitation_notice_id')}
              {field('Related Notice', opp.related_notice_id || opp.related_notice, 'related_notice_id')}
              {field('Current Version', opp.current_version ? `v${opp.current_version}` : 'v1')}
              {field('Procurement Case', opp.procurement_case_id)}
              <div style={{ marginTop: 8, padding: '6px 10px', background: '#f9fafb', borderRadius: 4, fontSize: 10, color: '#9ca3af' }}>
                System reference: {opp.internal_opp_id || opp.id?.substring(0, 8)}
              </div>
            </div>

            {/* Agency & Office */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 13, marginBottom: 14, color: '#374151', fontWeight: 600 }}>Agency & Office</h3>
              {agencyField(opp.agency || opp.department)}
              {field('Sub-tier', opp.sub_tier, 'sub_tier')}
              {field('Office', opp.office, 'office')}
              {field('Contracting Office Address', opp.contracting_office_address, 'contracting_office_address')}
            </div>

            {/* Dates & Deadlines */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 13, marginBottom: 14, color: '#374151', fontWeight: 600 }}>Dates & Deadlines</h3>
              {field('Published Date', opp.publication_date || opp.published_date || opp.posted_date, 'publication_date')}
              {field('Date Offers Due', opp.response_deadline || opp.date_offers_due, 'response_deadline')}
              {field('Inactive Date', opp.inactive_date, 'inactive_date')}
              {field('Inactive Policy', opp.inactive_policy, 'inactive_policy')}
              {field('Status', opp.status, 'status')}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            {/* Classification */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 13, marginBottom: 14, color: '#374151', fontWeight: 600 }}>Classification</h3>
              {field('NAICS Code', opp.naics_code, 'naics_code')}
              {field('Product Service Code', opp.psc_code || opp.product_service_code, 'psc_code')}
              {field('Set-Aside', opp.set_aside, 'set_aside')}
              {field('Place of Performance', opp.place_of_performance, 'place_of_performance')}
              {field('Initiative', opp.initiative, 'initiative')}
              {field('Contract Line Item Number', opp.contract_line_item_number, 'contract_line_item_number')}
            </div>

            {/* Financial */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 13, marginBottom: 14, color: '#374151', fontWeight: 600 }}>Financial</h3>
              {field('Budget Amount', opp.budget || opp.budget_amount, 'budget')}
              {field('Contract Type', opp.contract_type || opp.contract_opportunity_type, 'contract_type')}
              {field('Award PIID', opp.award_piid, 'award_piid')}
              {field('Solicitation Number', opp.solicitation_number, 'solicitation_number')}
            </div>

            {/* Points of Contact */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 13, marginBottom: 14, color: '#374151', fontWeight: 600 }}>Points of Contact</h3>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>Primary</div>
              {field('Name', opp.poc_primary_name, 'poc_primary_name')}
              {field('Email', opp.poc_primary_email, 'poc_primary_email')}
              {field('Phone', opp.poc_primary_phone, 'poc_primary_phone')}
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 6, marginTop: 12 }}>Alternative</div>
              {field('Name', opp.poc_alt_name, 'poc_alt_name')}
              {field('Email', opp.poc_alt_email, 'poc_alt_email')}
              {field('Phone', opp.poc_alt_phone, 'poc_alt_phone')}
            </div>
          </div>

          {/* Applicable Regulations — placed after the opportunity's general info so the
              user first sees which opportunity they're on, then the governing regulations. */}
          {id && <ApplicableRegulations opportunityId={id} />}

          <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: 13, marginBottom: 12, fontWeight: 600 }}>Description &amp; Notes</h3>
            {fieldArea('Description', opp.description, 'description')}
            {fieldArea('AI-Extracted Notes', opp.notes, 'notes')}
          </div>

          {/* Procurement Case & Relationship Tree */}
          <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 13, margin: 0, fontWeight: 600, color: '#374151' }}>Procurement Case & Related Notices</h3>
              <div style={{ display: 'flex', gap: 6 }}>
                {!opp.procurement_case_id && (
                  <button onClick={() => setShowLinkCase(true)} style={{ padding: '4px 10px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>+ Create / Link Case</button>
                )}
                <button onClick={() => setShowVersions(!showVersions)} style={{ padding: '4px 10px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>
                  {showVersions ? 'Hide Versions' : `Versions (${versions.length || 1})`}
                </button>
                <button onClick={() => setShowAmendForm(!showAmendForm)} style={{ padding: '4px 10px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>+ Amendment</button>
              </div>
            </div>

            {/* Link/Create Case Panel */}
            {showLinkCase && (
              <div style={{ padding: 12, background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 6, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#5b21b6', marginBottom: 8 }}>Link to Procurement Case</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={async () => {
                    const res = await api.post<any>(`/notices/${id}/link`, { action: 'create_new', title: opp.title, agency: opp.agency, opportunity_ids: [id] });
                    setMessage(`Created case: ${res.case_number || 'new case'}`);
                    setShowLinkCase(false);
                    loadOpportunity();
                  }} style={{ padding: '6px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Create New Case</button>
                  <button onClick={() => setShowLinkCase(false)} style={{ padding: '6px 12px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Amendment Form */}
            {showAmendForm && (
              <div style={{ padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>Create Amendment / New Version</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <input id="amend-number" placeholder="Amendment # (e.g. 0001)" aria-label="Amendment number" style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11 }} />
                  <input id="amend-deadline" type="date" placeholder="New deadline" aria-label="New deadline" style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11 }} />
                </div>
                <textarea id="amend-summary" placeholder="Summary of changes..." aria-label="Summary of changes" style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, minHeight: 40, boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={async () => {
                    const num = (document.getElementById('amend-number') as HTMLInputElement)?.value || '';
                    const deadline = (document.getElementById('amend-deadline') as HTMLInputElement)?.value || '';
                    const summary = (document.getElementById('amend-summary') as HTMLTextAreaElement)?.value || '';
                    const res = await api.post<any>(`/notices/${id}/amend`, { amendment_number: num, response_deadline: deadline, changes_summary: summary, title: opp.title });
                    setMessage(`Amendment v${res.version_number} created`);
                    setShowAmendForm(false);
                    loadOpportunity();
                  }} style={{ padding: '6px 12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Create Amendment</button>
                  <button onClick={() => setShowAmendForm(false)} style={{ padding: '6px 12px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Case info */}
            {opp.procurement_case_id && (
              <div style={{ padding: '8px 12px', background: '#f5f3ff', borderRadius: 4, marginBottom: 12, fontSize: 11 }}>
                <span style={{ color: '#6b7280' }}>Case:</span> <strong style={{ fontFamily: 'monospace', color: '#5b21b6' }}>{opp.procurement_case_id}</strong>
                {procCase && <span style={{ color: '#6b7280', marginLeft: 12 }}>| {procCase.title || procCase.agency || ''}</span>}
              </div>
            )}

            {/* Relationship Tree */}
            <div style={{ padding: '8px 0' }}>
              {/* Current notice */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#dbeafe', borderRadius: 4, border: '1px solid #93c5fd', marginBottom: 4 }}>
                <span style={{ fontSize: 12 }}>📋</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#1e40af' }}>{opp.notice_type || 'Notice'}</span>
                  <span style={{ fontSize: 11, color: '#374151', marginLeft: 8 }}>{opp.notice_id || opp.title}</span>
                </div>
                <span style={{ fontSize: 9, background: '#2563eb', color: 'white', padding: '1px 6px', borderRadius: 3 }}>Current · v{opp.current_version || 1}</span>
              </div>

              {/* Related notices */}
              {relatedNotices.length > 0 && relatedNotices.map((rn: any) => (
                <div key={rn.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginLeft: 20, borderLeft: '2px solid #c4b5fd', marginBottom: 4 }}>
                  <span style={{ fontSize: 12 }}>{(rn.notice_type || '').toLowerCase().includes('award') ? '🏆' : (rn.notice_type || '').toLowerCase().includes('rfi') || (rn.notice_type || '').toLowerCase().includes('sources') ? '🔍' : '📢'}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#6b7280' }}>{rn.notice_type || 'Notice'}</span>
                    <span style={{ fontSize: 11, color: '#374151', marginLeft: 6 }}>{rn.notice_id || rn.title?.substring(0, 40)}</span>
                  </div>
                  <Link to={`/opportunities/${rn.id}`} style={{ fontSize: 9, color: '#2563eb', textDecoration: 'none' }}>Open →</Link>
                </div>
              ))}
              {relatedNotices.length === 0 && !opp.procurement_case_id && (
                <div style={{ fontSize: 11, color: '#9ca3af', padding: '8px 0' }}>No related notices. Create a Procurement Case to group RFI, RFQ, and Award notices together.</div>
              )}
            </div>

            {/* Version History */}
            {showVersions && (
              <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Amendment / Version History</div>
                {versions.length === 0 ? (
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Original version only. No amendments recorded.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {versions.map((v: any) => (
                      <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: v.is_current ? '#f0fdf4' : '#f9fafb', borderRadius: 4, border: `1px solid ${v.is_current ? '#bbf7d0' : '#e5e7eb'}` }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: v.is_current ? '#065f46' : '#6b7280', minWidth: 24 }}>v{v.version_number}</span>
                        <div style={{ flex: 1, fontSize: 11 }}>
                          <span style={{ color: '#374151' }}>{v.amendment_number ? `Amendment ${v.amendment_number}` : v.version_number === 1 ? 'Original' : `Version ${v.version_number}`}</span>
                          {v.changes_summary && <span style={{ color: '#6b7280', marginLeft: 6 }}>— {v.changes_summary}</span>}
                        </div>
                        <span style={{ fontSize: 9, color: '#9ca3af' }}>{v.created_at ? new Date(v.created_at * 1000).toLocaleDateString() : ''}</span>
                        {v.is_current && <span style={{ fontSize: 8, background: '#10b981', color: 'white', padding: '1px 4px', borderRadius: 2 }}>current</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}


      {/* SOLICITATION TAB — procurement metadata from the analysis */}
      {activeTab === 'solicitation' && (
        <div>
          {/* Analyze button for solicitation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 14, margin: 0, color: '#374151' }}>Solicitation Information</h3>
              <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Procurement details, contract structure, FAR clauses, and submission instructions.</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ display: 'inline-block' }}>
                <input type="file" accept=".pdf,.docx,.doc,.txt,.xlsx,.csv" multiple onChange={handleDocumentUpload} disabled={uploading} style={{ display: 'none' }} />
                <div style={{ padding: '8px 14px', background: uploading ? '#e5e7eb' : '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: uploading ? 'default' : 'pointer', display: 'inline-block' }}>
                  {uploading ? '⏳ Uploading...' : '+ Upload Documents'}
                </div>
              </label>
              <button
                onClick={async () => { await runSolicitationAnalysis(); }}
                disabled={solicitationLoading}
                style={{ padding: '8px 14px', background: solicitationLoading ? '#93c5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: solicitationLoading ? 'default' : 'pointer' }}
              >
                {solicitationLoading ? '⏳ Analyzing...' : '▶ Analyze Procurement Data'}
              </button>
            </div>
          </div>
          {/* Uploaded docs list */}
          {documents.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Uploaded Documents</div>
              {documents.map((doc: any) => (
                <div key={doc.document_id || doc.id} style={{ padding: '4px 0', fontSize: 11, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{doc.filename}</span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ color: doc.extraction_status === 'completed' ? '#10b981' : '#f59e0b', fontSize: 10 }}>{doc.extraction_status || 'processed'}</span>
                    <button onClick={async () => {
                      if (!window.confirm(`Delete ${doc.filename}?`)) return;
                      try {
                        await api.del(`/documents/${doc.document_id || doc.id}`);
                        const docsData = await api.get<any>(`/documents?opportunity_id=${id}`);
                        setDocuments(docsData.items || []);
                      } catch (err) { console.warn('Delete failed:', err); }
                    }} style={{ padding: '1px 5px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 3, fontSize: 9, cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {solicitationAnalysis ? (
            <div>
              {/* Executive Summary */}
              {solicitationAnalysis.executive_summary && (
                <div style={{ background: '#f0f9ff', borderRadius: 8, padding: 16, border: '1px solid #bae6fd', marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0c4a6e', textTransform: 'uppercase', marginBottom: 6 }}>Executive Summary</div>
                  <div style={{ fontSize: 13, color: '#0f172a', lineHeight: 1.6 }}>{solicitationAnalysis.executive_summary}</div>
                </div>
              )}

              {/* Key Details */}
              {solicitationAnalysis.key_details && (
                <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>Key Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {solicitationAnalysis.key_details.solicitation_number && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Solicitation #</div>
                        <div style={{ fontSize: 13, marginTop: 2, fontWeight: 500 }}>{solicitationAnalysis.key_details.solicitation_number}</div>
                      </div>
                    )}
                    {solicitationAnalysis.key_details.issuing_office && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Issuing Office</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.key_details.issuing_office}</div>
                      </div>
                    )}
                    {solicitationAnalysis.key_details.offer_due_date && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Offer Due Date</div>
                        <div style={{ fontSize: 13, marginTop: 2, color: '#dc2626', fontWeight: 600 }}>{solicitationAnalysis.key_details.offer_due_date}</div>
                      </div>
                    )}
                    {solicitationAnalysis.key_details.naics_code && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>NAICS Code</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.key_details.naics_code}</div>
                      </div>
                    )}
                    {solicitationAnalysis.key_details.psc_code && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>PSC Code</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.key_details.psc_code}</div>
                      </div>
                    )}
                    {solicitationAnalysis.key_details.set_aside && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Set-Aside</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.key_details.set_aside}</div>
                      </div>
                    )}
                    {solicitationAnalysis.key_details.acquisition_type && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Acquisition Type</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.key_details.acquisition_type}</div>
                      </div>
                    )}
                    {solicitationAnalysis.key_details.point_of_contact?.name && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Point of Contact</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>
                          {solicitationAnalysis.key_details.point_of_contact.name}
                          {solicitationAnalysis.key_details.point_of_contact.email && (
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{solicitationAnalysis.key_details.point_of_contact.email}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contract Structure */}
              {solicitationAnalysis.contract_structure && (
                <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>Contract Structure</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {solicitationAnalysis.contract_structure.base_period && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Base Period</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.contract_structure.base_period}</div>
                      </div>
                    )}
                    {solicitationAnalysis.contract_structure.option_years && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Option Years</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.contract_structure.option_years}</div>
                      </div>
                    )}
                    {solicitationAnalysis.contract_structure.total_period_of_performance && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Total Period</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.contract_structure.total_period_of_performance}</div>
                      </div>
                    )}
                    {solicitationAnalysis.contract_structure.contract_type && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Contract Type</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.contract_structure.contract_type}</div>
                      </div>
                    )}
                    {solicitationAnalysis.contract_structure.evaluation_method && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Evaluation Method</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.contract_structure.evaluation_method}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Procurement Items */}
              {solicitationAnalysis.procurement_items?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>What's Being Procured</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {solicitationAnalysis.procurement_items.map((item: any, idx: number) => (
                      <div key={idx} style={{ padding: 12, background: '#f9fafb', borderRadius: 6, borderLeft: item.brand_name_or_equal ? '3px solid #f59e0b' : '3px solid #6b7280' }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{item.description}</div>
                        {item.quantity && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Qty: {item.quantity}</div>}
                        {item.specific_products && <div style={{ fontSize: 12, color: '#4f46e5', marginTop: 4 }}>Products: {item.specific_products}</div>}
                        {item.brand_name_or_equal && (
                          <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 3, fontWeight: 600 }}>
                            BRAND NAME OR EQUAL
                          </span>
                        )}
                        {item.notes && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{item.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Places of Performance */}
              {solicitationAnalysis.places_of_performance?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>Places of Performance</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {solicitationAnalysis.places_of_performance.map((place: any, idx: number) => (
                      <div key={idx} style={{ padding: '8px 12px', background: place.type === 'primary' ? '#dcfce7' : '#f3f4f6', borderRadius: 6, fontSize: 12 }}>
                        <span style={{ fontWeight: 500, color: '#1f2937' }}>{place.location}</span>
                        <span style={{ marginLeft: 6, fontSize: 10, color: '#6b7280', textTransform: 'uppercase' }}>({place.type})</span>
                        {place.notes && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{place.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Core Requirements / Salient Characteristics */}
              {solicitationAnalysis.core_requirements?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>
                    Core Requirements / Salient Characteristics ({solicitationAnalysis.core_requirements.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {solicitationAnalysis.core_requirements.map((req: any, idx: number) => (
                      <div key={idx} style={{ padding: 12, background: '#f9fafb', borderRadius: 6, borderLeft: `3px solid ${req.mandatory ? '#dc2626' : '#93c5fd'}` }}>
                        <div style={{ fontSize: 13, color: '#1f2937' }}>{req.requirement}</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <span style={{ fontSize: 10, background: '#e5e7eb', padding: '2px 6px', borderRadius: 3 }}>{req.category}</span>
                          {req.mandatory && <span style={{ fontSize: 10, background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: 3 }}>MANDATORY</span>}
                        </div>
                        {req.notes && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{req.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Contract Clauses */}
              {solicitationAnalysis.key_clauses?.length > 0 && (
                <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>Key Contract Clauses ({solicitationAnalysis.key_clauses.length})</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {solicitationAnalysis.key_clauses.map((clause: any, idx: number) => (
                      <div key={idx} style={{ padding: 10, background: '#f9fafb', borderRadius: 6, fontSize: 12 }}>
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>
                          {clause.clause_number}
                          {clause.title && <span style={{ fontWeight: 400, color: '#6b7280' }}> — {clause.title}</span>}
                        </div>
                        <div style={{ color: '#374151', marginTop: 4, lineHeight: 1.5 }}>{clause.summary}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submission Requirements */}
              {solicitationAnalysis.submission_requirements && (
                <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>How to Submit</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    {solicitationAnalysis.submission_requirements.submission_method && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Submission Method</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.submission_requirements.submission_method}</div>
                      </div>
                    )}
                    {solicitationAnalysis.submission_requirements.deadline && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Deadline</div>
                        <div style={{ fontSize: 13, marginTop: 2, color: '#dc2626', fontWeight: 600 }}>{solicitationAnalysis.submission_requirements.deadline}</div>
                      </div>
                    )}
                    {solicitationAnalysis.submission_requirements.page_limits && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Page Limits</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.submission_requirements.page_limits}</div>
                      </div>
                    )}
                    {solicitationAnalysis.submission_requirements.format_requirements && (
                      <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Format</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{solicitationAnalysis.submission_requirements.format_requirements}</div>
                      </div>
                    )}
                  </div>
                  {solicitationAnalysis.submission_requirements.required_documents?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Required Documents</div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#374151' }}>
                        {solicitationAnalysis.submission_requirements.required_documents.map((doc: string, idx: number) => (
                          <li key={idx} style={{ marginBottom: 4 }}>{doc}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {solicitationAnalysis.submission_requirements.special_instructions && (
                    <div style={{ marginTop: 12, padding: 10, background: '#fffbeb', borderRadius: 4, border: '1px solid #fde68a', fontSize: 12, color: '#78350f' }}>
                      <strong>Special Instructions:</strong> {solicitationAnalysis.submission_requirements.special_instructions}
                    </div>
                  )}
                </div>
              )}

              {/* Flags */}
              {solicitationAnalysis.flags && (solicitationAnalysis.flags.brand_name_or_equal_items?.length > 0 || solicitationAnalysis.flags.unusual_restrictions?.length > 0 || solicitationAnalysis.flags.potential_risks?.length > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                  {solicitationAnalysis.flags.brand_name_or_equal_items?.length > 0 && (
                    <div style={{ background: '#fffbeb', borderRadius: 8, padding: 16, border: '1px solid #fde68a' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 8 }}>Brand Name or Equal</div>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#78350f' }}>
                        {solicitationAnalysis.flags.brand_name_or_equal_items.map((item: string, idx: number) => (
                          <li key={idx} style={{ marginBottom: 3 }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {solicitationAnalysis.flags.unusual_restrictions?.length > 0 && (
                    <div style={{ background: '#fef2f2', borderRadius: 8, padding: 16, border: '1px solid #fecaca' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', marginBottom: 8 }}>Unusual Restrictions</div>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#dc2626' }}>
                        {solicitationAnalysis.flags.unusual_restrictions.map((item: string, idx: number) => (
                          <li key={idx} style={{ marginBottom: 3 }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {solicitationAnalysis.flags.potential_risks?.length > 0 && (
                    <div style={{ background: '#fff7ed', borderRadius: 8, padding: 16, border: '1px solid #fed7aa' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', marginBottom: 8 }}>Potential Risks</div>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#c2410c' }}>
                        {solicitationAnalysis.flags.potential_risks.map((item: string, idx: number) => (
                          <li key={idx} style={{ marginBottom: 3 }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Re-run button */}
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button
                  onClick={runSolicitationAnalysis}
                  disabled={solicitationLoading}
                  style={{ padding: '8px 16px', background: solicitationLoading ? '#c4b5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: solicitationLoading ? 'default' : 'pointer' }}
                >
                  {solicitationLoading ? '⏳ Re-analyzing...' : '🔄 Re-analyze Solicitation'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12, background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              Upload documents above and click "Run AI Analysis" to generate the solicitation analysis.
            </div>
          )}

          {/* Save Button — appears when changes are pending */}
          {Object.keys(pendingChanges).length > 0 && (
            <div style={{ position: 'sticky', bottom: 16, padding: '12px 20px', background: 'white', border: '1px solid #7c3aed', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', zIndex: 10, marginTop: 16 }}>
              <span style={{ fontSize: 13, color: '#5b21b6', fontWeight: 500 }}>
                {Object.keys(pendingChanges).length} unsaved change{Object.keys(pendingChanges).length > 1 ? 's' : ''}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setPendingChanges({}); window.location.reload(); }} style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                  Discard
                </button>
                <button onClick={handleSaveChanges} disabled={saving} style={{ padding: '8px 20px', background: saving ? '#93c5fd' : '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab === 'requirements' && (
        <div>
          {/* Upload & Analyze section */}
          <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 14, margin: 0, color: '#374151' }}>📎 Upload Documents & Extract Requirements</h3>
              <label style={{ display: 'inline-block' }}>
                <input type="file" accept=".pdf,.docx,.doc,.txt,.xlsx,.csv" multiple onChange={handleDocumentUpload} disabled={uploading} style={{ display: 'none' }} />
                <div style={{ padding: '8px 14px', background: uploading ? '#e5e7eb' : '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: uploading ? 'default' : 'pointer', display: 'inline-block' }}>
                  {uploading ? '⏳ Uploading...' : '+ Upload Documents'}
                </div>
              </label>
            </div>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>Upload RFI, RFQ, RFP, SOW, or amendments. AI will extract requirements, capabilities, and vendor profile.</p>
            {documents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                {documents.map((doc: any) => (
                  <div key={doc.document_id || doc.id} style={{ padding: '6px 10px', background: '#f9fafb', borderRadius: 4, fontSize: 11, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500 }}>{doc.filename}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 10, color: doc.extraction_status === 'completed' ? '#10b981' : '#f59e0b' }}>{doc.extraction_status || 'processed'}</span>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Delete ${doc.filename}?`)) return;
                          try {
                            await api.del(`/documents/${doc.document_id || doc.id}`);
                            setDocuments((prev: any[]) => prev.filter((d: any) => (d.document_id || d.id) !== (doc.document_id || doc.id)));
                          } catch (err: any) { alert(err.message); }
                        }}
                        style={{ padding: '2px 6px', background: 'transparent', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 4, fontSize: 10, cursor: 'pointer', lineHeight: 1 }}
                        title="Delete document"
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {documents.length > 0 ? (
              <button
                onClick={async () => { await runAnalysis(); if (documents.length) await runSolicitationAnalysis(); }}
                disabled={analyzing || solicitationLoading}
                style={{ padding: '10px 20px', background: (analyzing || solicitationLoading) ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: (analyzing || solicitationLoading) ? 'default' : 'pointer' }}
              >
                {analyzing || solicitationLoading ? '⏳ Analyzing...' : '▶ Extract Requirements'}
              </button>
            ) : (
              <button
                onClick={async () => { await runAnalysis(); await runSolicitationAnalysis(); }}
                disabled={analyzing || solicitationLoading}
                style={{ padding: '10px 20px', background: (analyzing || solicitationLoading) ? '#93c5fd' : '#6366f1', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: (analyzing || solicitationLoading) ? 'default' : 'pointer' }}
              >
                {analyzing || solicitationLoading ? '⏳ Analyzing...' : '▶ Analyze from Description'}
              </button>
            )}
          </div>

          {/* Requirements results */}
          {analysis && (
          <div>
          <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, marginBottom: 12, color: '#374151' }}>Opportunity Analysis</h3>
            
            {/* Executive Summary */}
            <div style={{ marginBottom: 16, padding: 14, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
              <div style={{ fontSize: 11, color: '#0369a1', fontWeight: 600, marginBottom: 6 }}>EXECUTIVE SUMMARY</div>
              <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.6 }}>{analysis.output?.executive_summary || analysis.output?.scope || 'N/A'}</div>
            </div>

            {/* Key Points */}
            {analysis.output?.key_points && analysis.output.key_points.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>KEY POINTS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {analysis.output.key_points.map((kp: string, i: number) => (
                    <div key={i} style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                      <strong>Key Point:</strong> {kp}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scope */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>SCOPE</div>
              <div style={{ fontSize: 13, marginTop: 4, color: '#374151', whiteSpace: 'pre-wrap' }}>{analysis.output?.scope || 'N/A'}</div>
            </div>

            {/* Ideal Vendor Profile */}
            {analysis.output?.ideal_vendor_profile && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 11, color: '#166534', fontWeight: 600, marginBottom: 4 }}>IDEAL VENDOR PROFILE</div>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{analysis.output.ideal_vendor_profile}</div>
              </div>
            )}
          </div>

          {/* Detailed Capabilities */}
          {analysis.output?.detailed_capabilities && analysis.output.detailed_capabilities.length > 0 && (
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16, marginTop: 16 }}>
              <h3 style={{ fontSize: 13, marginBottom: 12, color: '#374151' }}>
                What the Government Seeks ({analysis.output.detailed_capabilities.length} capabilities)
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {analysis.output.detailed_capabilities.map((cap: string, i: number) => (
                  <span key={i} style={{ padding: '3px 10px', background: '#dbeafe', color: '#1e40af', borderRadius: 9999, fontSize: 11 }}>{cap}</span>
                ))}
              </div>
            </div>
          )}

          {/* Vendor Differentiators */}
          {analysis.output?.vendor_differentiators && analysis.output.vendor_differentiators.length > 0 && (
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <h3 style={{ fontSize: 13, marginBottom: 12, color: '#374151' }}>Strongest Vendor Differentiators</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {analysis.output.vendor_differentiators.map((d: string, i: number) => (
                  <span key={i} style={{ padding: '3px 10px', background: '#d1fae5', color: '#065f46', borderRadius: 9999, fontSize: 11 }}>{d}</span>
                ))}
              </div>
            </div>
          )}

          {/* Requirements List with Full Traceability */}
          <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, margin: 0, color: '#374151' }}>
                Requirements ({analysis.output?.requirements?.length || 0})
              </h3>
              {analysis.output?._metadata && (
                <div style={{ fontSize: 10, color: '#64748b', textAlign: 'right' }}>
                  <div>Model: {analysis.output._metadata.model_version}</div>
                  <div>Extracted: {analysis.output._metadata.analyzed_at_iso}</div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {analysis.output?.requirements?.slice(0, 40).map((req: any, idx: number) => {
                const statusColors: Record<string, [string, string]> = {
                  pending: ['#fef3c7', '#92400e'],
                  confirmed: ['#d1fae5', '#065f46'],
                  corrected: ['#dbeafe', '#1e40af'],
                  rejected: ['#fee2e2', '#991b1b'],
                };
                const [bg, fg] = statusColors[req.validation_status] || statusColors.pending;
                return (
                <div key={req.id || idx} style={{ padding: 14, background: '#fafbfc', borderRadius: 8, borderLeft: `4px solid ${req.mandatory ? '#dc2626' : '#6366f1'}`, border: '1px solid #e5e7eb' }}>
                  {/* Requirement text */}
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937', marginBottom: 8 }}>{req.text}</div>

                  {/* Tags row */}
                  <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{ background: '#e5e7eb', padding: '2px 8px', borderRadius: 4, fontWeight: 500 }}>{req.category || 'general'}</span>
                    {req.mandatory && <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>MANDATORY</span>}
                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 4 }}>Confidence: {Math.round((req.confidence || 0) * 100)}%</span>
                    <span style={{ background: bg, color: fg, padding: '2px 8px', borderRadius: 4, fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>{req.validation_status || 'pending'}</span>
                    {req.id && <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: 10 }}>{req.id}</span>}
                  </div>

                  {/* Traceability metadata */}
                  <div style={{ fontSize: 11, color: '#64748b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', marginBottom: 8, padding: '8px 10px', background: '#f8fafc', borderRadius: 4 }}>
                    <div><strong>Source:</strong> {req.source_document || '—'}</div>
                    <div><strong>Page/Section:</strong> {req.source_page || '—'}</div>
                    <div><strong>Model:</strong> {req.model_version || '—'}</div>
                    <div><strong>Extracted:</strong> {req.extraction_timestamp_iso || '—'}</div>
                    {req.confirmed_by && <div><strong>Reviewed by:</strong> {req.confirmed_by}</div>}
                    {req.confirmed_at_iso && <div><strong>Reviewed at:</strong> {req.confirmed_at_iso}</div>}
                  </div>

                  {/* Supporting passage */}
                  {req.supporting_passage && req.supporting_passage !== 'AI-inferred from document context' && (
                    <div style={{ fontSize: 11, color: '#475569', padding: '8px 10px', background: '#fffbeb', borderRadius: 4, borderLeft: '3px solid #fbbf24', marginBottom: 8 }}>
                      <strong>Supporting passage:</strong> "{req.supporting_passage}"
                    </div>
                  )}

                  {/* Correction note */}
                  {req.correction_note && (
                    <div style={{ fontSize: 11, color: '#1e40af', padding: '6px 10px', background: '#eff6ff', borderRadius: 4, marginBottom: 8 }}>
                      <strong>Note:</strong> {req.correction_note}
                    </div>
                  )}

                  {/* Edit history */}
                  {req.edit_history && req.edit_history.length > 0 && (
                    <details style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '4px 0' }}>Edit History ({req.edit_history.length})</summary>
                      <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 12 }}>
                        {req.edit_history.map((h: any, hi: number) => (
                          <div key={hi} style={{ padding: '4px 8px', background: '#f9fafb', borderRadius: 4, borderLeft: '2px solid #d1d5db' }}>
                            <span style={{ fontWeight: 500 }}>{h.action}</span> by {h.user} at {h.timestamp_iso}
                            {h.detail && <span> — {h.detail}</span>}
                            {h.new_text && <div style={{ marginTop: 2, color: '#059669' }}>New: "{h.new_text}"</div>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Action buttons */}
                  {req.validation_status !== 'confirmed' && req.id && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/opportunities/${id}/requirements`, { requirement_id: req.id, action: 'confirm', user: 'admin' });
                            setMessage(`✓ Requirement ${req.id} confirmed`);
                            const oppRes = await opportunitiesApi.get(id!); const a = oppRes.analysis ? (typeof oppRes.analysis === 'string' ? JSON.parse(oppRes.analysis) : oppRes.analysis) : null; if (a) setAnalysis({ output: a });
                          } catch (e: any) { setError(e.message); }
                        }}
                        aria-label={`Confirm requirement ${req.id}`}
                        style={{ padding: '4px 10px', background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}
                      >Confirm</button>
                      <button
                        onClick={async () => {
                          const note = window.prompt('Why is this requirement inaccurate?');
                          if (note === null) return;
                          try {
                            await api.post(`/opportunities/${id}/requirements`, { requirement_id: req.id, action: 'reject', user: 'admin', note });
                            setMessage(`Requirement ${req.id} rejected`);
                            const oppRes = await opportunitiesApi.get(id!); const a = oppRes.analysis ? (typeof oppRes.analysis === 'string' ? JSON.parse(oppRes.analysis) : oppRes.analysis) : null; if (a) setAnalysis({ output: a });
                          } catch (e: any) { setError(e.message); }
                        }}
                        aria-label={`Reject requirement ${req.id}`}
                        style={{ padding: '4px 10px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}
                      >Reject</button>
                      <button
                        onClick={async () => {
                          const newText = window.prompt('Corrected requirement text:', req.text);
                          if (!newText) return;
                          const note = window.prompt('Correction note (optional):') || '';
                          try {
                            await api.post(`/opportunities/${id}/requirements`, { requirement_id: req.id, action: 'correct', user: 'admin', correction_text: newText, note });
                            setMessage(`✓ Requirement ${req.id} corrected`);
                            const oppRes = await opportunitiesApi.get(id!); const a = oppRes.analysis ? (typeof oppRes.analysis === 'string' ? JSON.parse(oppRes.analysis) : oppRes.analysis) : null; if (a) setAnalysis({ output: a });
                          } catch (e: any) { setError(e.message); }
                        }}
                        aria-label={`Edit requirement ${req.id}`}
                        style={{ padding: '4px 10px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}
                      >Edit</button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
          )}
        </div>
      )}

      {/* ANALYSIS TAB */}
      {activeTab === 'analysis' && analysis && (
        <div>
          <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>Analysis Summary</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Certifications Required</div>
                <div style={{ fontSize: 13, color: '#374151' }}>
                  {analysis.output?.certifications?.length ? analysis.output.certifications.join(', ') : 'None specified'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Contract Vehicles</div>
                <div style={{ fontSize: 13, color: '#374151' }}>
                  {analysis.output?.contract_vehicles?.length ? analysis.output.contract_vehicles.join(', ') : 'None specified'}
                </div>
              </div>
            </div>

            {analysis.output?.risks?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#dc2626', marginBottom: 8 }}>⚠️ Identified Risks</div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#374151' }}>
                  {analysis.output.risks.map((risk: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: 4 }}>{risk}</li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.output?.ambiguities?.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#f59e0b', marginBottom: 8 }}>❓ Ambiguities</div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#374151' }}>
                  {analysis.output.ambiguities.map((amb: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: 4 }}>{amb}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* INTAKE TAB — FAR Part 11 */}
      {activeTab === 'intake' && (
        <div>
          <IntakeTab opp={opp} oppId={id!} onSaved={(intake) => setOpp({ ...opp, intake_data: JSON.stringify(intake) })} />
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
            <DraftGeneratorTab opp={opp} oppId={id!} analysis={analysis} documents={documents}
              onSaved={(drafts) => setOpp({ ...opp, solicitation_drafts: JSON.stringify(drafts) })}
              stages={['intake', 'requirements', 'market_research']} heading="Planning & Requirements Documents" />
          </div>
        </div>
      )}

      {/* CONTRACT ADMINISTRATION & CLOSEOUT TAB — post-award (FAR 42/43/46/32/49/4.804) */}
      {activeTab === 'contract_admin' && (
        <div>
          {!awardFinalized && (
            <div style={{ padding: '12px 16px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#9a3412', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span><strong>Post-award stage.</strong> Contract administration and closeout begin after an award is finalized. Finalize the award in Selection &amp; Evaluation to activate this stage. You can review the sections below, but they apply once a contract exists.</span>
              <button onClick={() => setActiveTab('evaluate')} style={{ padding: '7px 14px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Go to Selection &amp; Evaluation →</button>
            </div>
          )}
          <ContractAdminTab opp={opp} oppId={id!} awardFinalized={awardFinalized} onSaved={(ca) => setOpp({ ...opp, contract_admin: JSON.stringify(ca) })} />
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
            <DraftGeneratorTab opp={opp} oppId={id!} analysis={analysis} documents={documents}
              onSaved={(drafts) => setOpp({ ...opp, solicitation_drafts: JSON.stringify(drafts) })}
              stages={['administration_closeout']} heading="Contract Admin & Closeout Documents" />
          </div>
        </div>
      )}

      {/* SOLICITATION DEVELOPMENT SUB-TAB — solicitation documents only. The RFQ
          develops from the Sources Sought created on the Intake tab (read from the
          saved opportunity drafts), so each document stays in its own stage. */}
      {activeTab === 'draft' && (
        <div>
          <ClauseSuggester oppId={id!} />
          <DraftGeneratorTab opp={opp} oppId={id!} analysis={analysis} documents={documents}
            onSaved={(drafts) => setOpp({ ...opp, solicitation_drafts: JSON.stringify(drafts) })}
            stages={['solicitation']} heading="Solicitation Documents" />
        </div>
      )}

      {/* MATCHES TAB */}
      {activeTab === 'matches' && (
        <div>
          {/* Match Vendors button */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 14, margin: 0, color: '#374151' }}>Vendor Matching</h3>
              <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>AI matches vendors from your database against the extracted requirements.</p>
            </div>
            <button onClick={runMatching} disabled={matching} style={{ padding: '10px 20px', background: matching ? '#86efac' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: matching ? 'default' : 'pointer' }}>
              {matching ? '⏳ Matching...' : '🎯 Match Vendors'}
            </button>
          </div>
          {!analysis && <div style={{ padding: 16, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, color: '#1e40af', marginBottom: 16 }}>Matching uses this opportunity's title, description, NAICS/PSC, and any uploaded documents. For richer results, upload the notice documents in the Documents panel first — but you can match now.</div>}
          {matches.length > 0 ? (
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 14, marginBottom: 4, color: '#374151' }}>FIT Score Rankings ({matches.length})</h3>
              <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 16 }}>
                FIT = (0.30×Mission) + (0.20×Domain) + (0.20×Deployment) + (0.15×Completeness) + (0.15×Contract)
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {matches.slice(0, 20).map((match: any, idx: number) => {
                  const explanation = match.fit_explanation || match;
                  const strengths = explanation.strengths || match.why_strong || [];
                  const gaps = explanation.capability_gaps || match.gaps || [];
                  const differentiator = explanation.key_differentiator || match.key_differentiator || match.differentiators || '';
                  const role = explanation.recommended_role || match.recommended_role || '';
                  const teaming = explanation.teaming_recommendation || match.teaming_recommendation || '';
                  const mc = match.mission_capability_score || match.mission_capability || 0;
                  const da = match.domain_alignment_score || match.domain_alignment || 0;
                  const dr = match.deployment_reality_score || match.deployment_reality || 0;
                  const sc = match.solution_completeness_score || match.solution_completeness || 0;
                  const cf = match.contract_fit_score || match.contract_fit || 0;
                  const fitScore = match.fit_score || 0;
                  const classification = match.fit_classification || 'Unscored';

                  return (
                    <div key={match.vendor_match_id || match.vendor_id || idx} style={{ padding: 16, background: '#f9fafb', borderRadius: 6, borderLeft: `4px solid ${fitScore >= 92 ? '#10b981' : fitScore >= 87 ? '#3b82f6' : fitScore >= 82 ? '#6366f1' : '#f59e0b'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                            #{idx + 1} {match.vendor_name}
                            {role && (
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: '#6b7280', textTransform: 'capitalize' }}>
                                ({role.replace(/_/g, ' ')})
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>FIT: {fitScore}/100</div>
                        </div>
                        <div style={{ background: fitScore >= 92 ? '#dcfce7' : fitScore >= 87 ? '#dbeafe' : fitScore >= 82 ? '#e0e7ff' : '#fef3c7', color: fitScore >= 92 ? '#166534' : fitScore >= 87 ? '#1e40af' : fitScore >= 82 ? '#3730a3' : '#92400e', padding: '4px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                          {classification}
                        </div>
                      </div>

                      {/* Score breakdown */}
                      <div style={{ fontSize: 11, color: '#4b5563', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                        <div>MC: {mc}</div>
                        <div>DA: {da}</div>
                        <div>DR: {dr}</div>
                        <div>SC: {sc}</div>
                        <div>CF: {cf}</div>
                      </div>

                      {/* Key Differentiator */}
                      {differentiator && (
                        <div style={{ fontSize: 12, color: '#1f2937', marginBottom: 6, fontStyle: 'italic' }}>
                          <strong>Key Differentiator:</strong> {differentiator}
                        </div>
                      )}

                      {/* Strengths & Gaps */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
                        {strengths.length > 0 && (
                          <div>
                            <span style={{ color: '#10b981', fontWeight: 600 }}>Why the match is strong:</span>
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: 14, color: '#374151' }}>
                              {strengths.slice(0, 3).map((s: string, i: number) => (
                                <li key={i} style={{ marginBottom: 2 }}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {gaps.length > 0 && (
                          <div>
                            <span style={{ color: '#dc2626', fontWeight: 600 }}>Key gaps identified:</span>
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: 14, color: '#374151' }}>
                              {gaps.slice(0, 3).map((g: string, i: number) => (
                                <li key={i} style={{ marginBottom: 2 }}>{g}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Teaming Recommendation */}
                      {teaming && (
                        <div style={{ marginTop: 6, fontSize: 11, color: '#4338ca', background: '#eef2ff', padding: '4px 8px', borderRadius: 4 }}>
                          <strong>Teaming:</strong> {teaming}
                        </div>
                      )}

                      {/* Deep Analysis Button */}
                      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            if (deepAnalysis[match.vendor_id] && expandedVendor === match.vendor_id) {
                              setExpandedVendor(null);
                            } else if (deepAnalysis[match.vendor_id]) {
                              setExpandedVendor(match.vendor_id);
                            } else {
                              runDeepAnalysis(match.vendor_id, match.vendor_name);
                            }
                          }}
                          disabled={deepAnalyzing[match.vendor_id]}
                          style={{
                            padding: '6px 12px',
                            background: deepAnalyzing[match.vendor_id] ? '#93c5fd' : '#1e40af',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: deepAnalyzing[match.vendor_id] ? 'default' : 'pointer',
                          }}
                        >
                          {deepAnalyzing[match.vendor_id] ? '⏳ Analyzing...' : deepAnalysis[match.vendor_id] ? (expandedVendor === match.vendor_id ? '▼ Hide Deep Analysis' : '▶ Show Deep Analysis') : '🔍 Run Deep Analysis'}
                        </button>
                      </div>

                      {/* Deep Analysis Expanded View */}
                      {expandedVendor === match.vendor_id && deepAnalysis[match.vendor_id] && (() => {
                        const da = deepAnalysis[match.vendor_id];
                        return (
                          <div style={{ marginTop: 12, borderTop: '2px solid #2563eb', paddingTop: 12 }}>
                            {/* Executive Summary */}
                            {da.executive_summary && (
                              <div style={{ background: '#f0f9ff', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #bae6fd' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#0c4a6e', textTransform: 'uppercase', marginBottom: 4 }}>Executive Summary</div>
                                <div style={{ fontSize: 12, color: '#0f172a', lineHeight: 1.6 }}>{da.executive_summary}</div>
                              </div>
                            )}

                            {/* Go/No-Go Decision */}
                            {da.go_no_go && (
                              <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 4, marginBottom: 12, fontSize: 12, fontWeight: 700, background: da.go_no_go === 'GO' ? '#dcfce7' : da.go_no_go === 'GO_SELECTIVE' ? '#dbeafe' : da.go_no_go === 'EDUCATE' ? '#fef3c7' : '#fee2e2', color: da.go_no_go === 'GO' ? '#166534' : da.go_no_go === 'GO_SELECTIVE' ? '#1e40af' : da.go_no_go === 'EDUCATE' ? '#92400e' : '#991b1b' }}>
                                Decision: {da.go_no_go}
                              </div>
                            )}
                            {da.go_no_go_rationale && (
                              <div style={{ fontSize: 11, color: '#475569', marginBottom: 12 }}>{da.go_no_go_rationale}</div>
                            )}

                            {/* FIT Scores Detail */}
                            {da.fit_scores && (
                              <div style={{ background: '#f8fafc', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', textTransform: 'uppercase', marginBottom: 8 }}>FIT Score Breakdown</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {[
                                    { key: 'mission_capability', label: 'Mission Capability (30%)', color: '#2563eb' },
                                    { key: 'domain_alignment', label: 'Domain Alignment (20%)', color: '#7c3aed' },
                                    { key: 'deployment_reality', label: 'Deployment Reality (20%)', color: '#059669' },
                                    { key: 'solution_completeness', label: 'Solution Completeness (15%)', color: '#d97706' },
                                    { key: 'contract_fit', label: 'Contract Fit (15%)', color: '#dc2626' },
                                  ].map(dim => (
                                    <div key={dim.key}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                                        <span style={{ color: '#475569' }}>{dim.label}</span>
                                        <span style={{ fontWeight: 600, color: dim.color }}>{da.fit_scores[dim.key]}/100</span>
                                      </div>
                                      <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2 }}>
                                        <div style={{ height: '100%', width: `${da.fit_scores[dim.key] || 0}%`, background: dim.color, borderRadius: 2 }} />
                                      </div>
                                      {da.fit_scores[`${dim.key}_rationale`] && (
                                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{da.fit_scores[`${dim.key}_rationale`]}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Strengths */}
                            {da.strengths?.length > 0 && (
                              <div style={{ background: '#f0fdf4', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #bbf7d0' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 8 }}>Strengths</div>
                                {da.strengths.map((s: any, i: number) => (
                                  <div key={i} style={{ marginBottom: 6, fontSize: 11 }}>
                                    <div style={{ fontWeight: 600, color: '#15803d' }}>{typeof s === 'string' ? s : s.strength}</div>
                                    {s.evidence && <div style={{ color: '#475569', marginTop: 1 }}>Evidence: {s.evidence}</div>}
                                    {s.relevance && <div style={{ color: '#475569', marginTop: 1 }}>Relevance: {s.relevance}</div>}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Gaps & Mitigations */}
                            {da.gaps_and_mitigations?.length > 0 && (
                              <div style={{ background: '#fff7ed', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #fed7aa' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', marginBottom: 8 }}>Gaps & Mitigations</div>
                                {da.gaps_and_mitigations.map((g: any, i: number) => (
                                  <div key={i} style={{ marginBottom: 8, fontSize: 11, padding: '6px 8px', background: 'white', borderRadius: 4, borderLeft: `3px solid ${g.severity === 'critical' ? '#dc2626' : g.severity === 'major' ? '#f59e0b' : '#6b7280'}` }}>
                                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{typeof g === 'string' ? g : g.gap}</div>
                                    {g.severity && <span style={{ fontSize: 10, color: g.severity === 'critical' ? '#dc2626' : '#f59e0b', textTransform: 'uppercase' }}>{g.severity}</span>}
                                    {g.mitigation && <div style={{ color: '#059669', marginTop: 2 }}>Mitigation: {g.mitigation}</div>}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Teaming Architecture */}
                            {da.teaming_architecture && (
                              <div style={{ background: '#eef2ff', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #c7d2fe' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#3730a3', textTransform: 'uppercase', marginBottom: 8 }}>Teaming Architecture</div>
                                {da.teaming_architecture.recommended_prime && (
                                  <div style={{ fontSize: 11, marginBottom: 6 }}><strong>Recommended Prime:</strong> {da.teaming_architecture.recommended_prime}</div>
                                )}
                                {da.teaming_architecture.teaming_partners?.map((p: any, i: number) => (
                                  <div key={i} style={{ fontSize: 11, marginBottom: 4, padding: '4px 8px', background: 'white', borderRadius: 4 }}>
                                    <strong>{p.partner}</strong> — {p.role} {p.why && <span style={{ color: '#6b7280' }}>({p.why})</span>}
                                  </div>
                                ))}
                                {da.teaming_architecture.teaming_rationale && (
                                  <div style={{ fontSize: 11, color: '#475569', marginTop: 6 }}>{da.teaming_architecture.teaming_rationale}</div>
                                )}
                              </div>
                            )}

                            {/* Risks */}
                            {da.risks?.length > 0 && (
                              <div style={{ background: '#fef2f2', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #fecaca' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', marginBottom: 8 }}>Risks</div>
                                {da.risks.map((r: any, i: number) => (
                                  <div key={i} style={{ fontSize: 11, marginBottom: 4 }}>
                                    <span style={{ fontWeight: 600 }}>{typeof r === 'string' ? r : r.risk}</span>
                                    {r.likelihood && <span style={{ color: '#6b7280' }}> (L: {r.likelihood}, I: {r.impact})</span>}
                                    {r.mitigation && <div style={{ color: '#059669', fontSize: 10, marginTop: 1 }}>→ {r.mitigation}</div>}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Competitive Landscape */}
                            {da.competitive_landscape && (
                              <div style={{ background: '#f8fafc', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', textTransform: 'uppercase', marginBottom: 4 }}>Competitive Landscape</div>
                                <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>{da.competitive_landscape}</div>
                              </div>
                            )}

                            {/* Capture Actions */}
                            {da.capture_actions?.length > 0 && (
                              <div style={{ background: '#ecfdf5', borderRadius: 6, padding: 12, border: '1px solid #a7f3d0' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#065f46', textTransform: 'uppercase', marginBottom: 8 }}>Capture Actions</div>
                                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#1f2937' }}>
                                  {da.capture_actions.map((a: string, i: number) => (
                                    <li key={i} style={{ marginBottom: 4 }}>{a}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 40, textAlign: 'center', color: '#6b7280', border: '1px dashed #d1d5db' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🎯</div>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: '0 0 4px' }}>No vendor matches yet</p>
              <p style={{ fontSize: 12, margin: '0 0 16px' }}>Click <strong>Match Vendors</strong> to have the AI rank vendors from your database against this opportunity's NAICS/PSC, requirements, and documents.</p>
              <button onClick={runMatching} disabled={matching} style={{ padding: '10px 20px', background: matching ? '#86efac' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: matching ? 'default' : 'pointer' }}>
                {matching ? '⏳ Matching...' : '🎯 Match Vendors'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* EVALUATE TAB (Selection & Evaluation) */}
      {activeTab === 'evaluate' && (
        <div>
          <div style={{ background: '#eff6ff', borderRadius: 8, padding: 16, marginBottom: 20, border: '1px solid #bfdbfe' }}>
            <p style={{ fontSize: 13, color: '#1e40af', margin: 0 }}>
              <strong>Selection &amp; Evaluation (source-selection decision).</strong> Add vendors who submitted responses, upload their proposals, run AI evaluation against the requirements, and select the awardee. When the award decision is final, use <strong>Finalize Award</strong> below to open the <strong>Contract Admin &amp; Closeout</strong> stage.
            </p>
          </div>

          {/* Finalize Award — flips status to 'awarded' and opens the post-award stage */}
          <div style={{ background: opp.status === 'awarded' ? '#f0fdf4' : 'white', borderRadius: 8, padding: 16, marginBottom: 20, border: `1px solid ${opp.status === 'awarded' ? '#bbf7d0' : '#e5e7eb'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <h3 style={{ fontSize: 13, margin: 0, color: '#374151' }}>Award Decision</h3>
              <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {opp.status === 'awarded'
                  ? '✓ Award finalized. The Contract Admin & Closeout tab is now active.'
                  : 'Finalizing sets this opportunity to "awarded" and opens the Contract Administration & Closeout stage. Only do this once the source-selection decision is final.'}
              </p>
            </div>
            {opp.status !== 'awarded' && (
              <button
                onClick={async () => {
                  const piid = window.prompt('Award ID / PIID (optional):', opp.award_piid || '') || '';
                  if (!window.confirm('Finalize the award? This sets the opportunity to "awarded" and opens the Contract Admin & Closeout stage.')) return;
                  try {
                    const payload: any = { status: 'awarded' };
                    if (piid) payload.award_piid = piid;
                    await opportunitiesApi.update(id!, payload);
                    setOpp((prev: any) => ({ ...prev, status: 'awarded', award_piid: piid || prev.award_piid }));
                    setMessage('✓ Award finalized. Contract Admin & Closeout is now available.');
                    setActiveTab('contract_admin');
                  } catch (e: any) {
                    setError(e.message || 'Failed to finalize award.');
                  }
                }}
                style={{ padding: '10px 20px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                🏆 Finalize Award
              </button>
            )}
            {opp.status === 'awarded' && (
              <button
                onClick={() => setActiveTab('contract_admin')}
                style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                📑 Go to Contract Admin
              </button>
            )}
          </div>

          {/* Award-stage documents (notification letters) live with the award decision. */}
          <div style={{ marginBottom: 20 }}>
            <DraftGeneratorTab opp={opp} oppId={id!} analysis={analysis} documents={documents}
              onSaved={(drafts) => setOpp({ ...opp, solicitation_drafts: JSON.stringify(drafts) })}
              stages={['award']} heading="Award Documents" />
          </div>

          {/* Add Vendor Response */}
          <div style={{ background: 'white', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 13, margin: 0, color: '#374151' }}>Add Vendor Response</h3>
            </div>
            <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  id="eval-vendor-search"
                  placeholder="Search vendor from database..."
                  aria-label="Search vendor from database"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
                  onChange={async (e) => {
                    const val = (e.target as HTMLInputElement).value.trim();
                    const resultsEl = document.getElementById('eval-vendor-dropdown');
                    if (val.length < 2) {
                      if (resultsEl) resultsEl.style.display = 'none';
                      return;
                    }
                    try {
                      const res = await api.get<any>(`/vendors?search=${encodeURIComponent(val)}&page_size=10`);
                      const items = res.items || res.vendors || [];
                      if (resultsEl) {
                        resultsEl.innerHTML = items.length === 0 
                          ? '<div style="padding:8px 12px;color:#9ca3af;font-size:11px;">No vendors found</div>'
                          : items.map((v: any) => `<div class="vendor-dropdown-item" data-id="${v.vendor_id || v.id}" data-name="${v.name || v.Vendor || ''}" style="padding:8px 12px;cursor:pointer;font-size:12px;border-bottom:1px solid #f3f4f6;transition:background 0.1s;" onmouseover="this.style.background='#eff6ff'" onmouseout="this.style.background='white'">${v.name || v.Vendor || 'Unknown'} <span style="color:#9ca3af;font-size:10px;">${v.cage_code || v['CAGE/NCAGE'] || ''}</span></div>`).join('');
                        resultsEl.style.display = 'block';
                        // Add click handlers
                        setTimeout(() => {
                          resultsEl.querySelectorAll('.vendor-dropdown-item').forEach((item: any) => {
                            item.onclick = () => {
                              const vendorId = item.getAttribute('data-id');
                              const vendorName = item.getAttribute('data-name');
                              // Check if already in matches
                              if (matches.find((m: any) => m.vendor_id === vendorId)) {
                                setMessage(`⚠️ ${vendorName} is already in the evaluation list`);
                              } else {
                                const newMatch = { vendor_id: vendorId, vendor_name: vendorName, fit_score: 0, fit_classification: 'Added for Evaluation' };
                                const updatedMatches = [...matches, newMatch];
                                setMatches(updatedMatches);
                                // Persist to opportunity record
                                api.put(`/opportunities/${id}`, { evaluation_vendors: updatedMatches }).catch(err => console.error('Failed to save eval vendors:', err));
                                setMessage(`✓ ${vendorName} added to evaluation`);
                              }
                              (document.getElementById('eval-vendor-search') as HTMLInputElement).value = '';
                              resultsEl.style.display = 'none';
                            };
                          });
                        }, 50);
                      }
                    } catch (err) {
                      console.error('Vendor search error:', err);
                    }
                  }}
                  onBlur={() => {
                    // Delay hiding to allow click on dropdown items
                    setTimeout(() => {
                      const el = document.getElementById('eval-vendor-dropdown');
                      if (el) el.style.display = 'none';
                    }, 200);
                  }}
                />
                <div id="eval-vendor-dropdown" style={{ display: 'none', position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #d1d5db', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: 200, overflowY: 'auto' }}></div>
              </div>
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>Search for a vendor from your database. Select from the dropdown to add them to the evaluation list.</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {matches.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 13 }}>No vendors added yet. Add vendors above to upload their responses.</div>
                <div style={{ fontSize: 11, marginTop: 8 }}>You can also use "Match Vendors" to get AI recommendations first.</div>
              </div>
            )}
            {matches.map((match: any) => {
              const vendorId = match.vendor_id;
              const evalData = vendorEvalData[vendorId];
              const isUploading = vendorUploading[vendorId];
              const isAnalyzing = vendorAnalyzing[vendorId];
              const evaluation = evalData?.evaluation;

              // Load evaluation data on first render
              if (!evalData && !vendorUploading[vendorId]) {
                loadVendorEvaluation(vendorId);
              }

              return (
                <div key={vendorId} style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                  {/* Vendor Header */}
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9fafb' }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}><Link to={`/vendors/${match.vendor_id}`} style={{ color: '#1d4ed8', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>{match.vendor_name}</Link></div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        FIT Score: {Math.round((evalData?.evaluation?.confidence_score || match.fit_score || 0) * (match.fit_score ? 1 : 100))}/100 - {evalData?.evaluation?.recommendation ? evalData.evaluation.recommendation.toUpperCase() : match.fit_classification || 'Pending Evaluation'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {evaluation?.recommendation && (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          background: evaluation.recommendation === 'proceed' ? '#dcfce7' : evaluation.recommendation === 'reject' ? '#fee2e2' : '#fef3c7',
                          color: evaluation.recommendation === 'proceed' ? '#166534' : evaluation.recommendation === 'reject' ? '#991b1b' : '#92400e',
                        }}>
                          {evaluation.recommendation}
                        </span>
                      )}
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Select ${match.vendor_name} as the awardee for this opportunity?`)) return;
                          try {
                            // Create award record
                            await api.post<any>('/awards', {
                              lifecycle_id: lifecycle?.id,
                              vendor_name: match.vendor_name,
                              vendor_id: vendorId,
                              award_amount: 0,
                              contract_number: opp?.notice_id || '',
                              status: 'active',
                              contract_type: opp?.contract_type || opp?.contract_opportunity_type || '',
                            });
                            // Update opportunity status to awarded
                            await opportunitiesApi.update(id!, { status: 'awarded' });
                            // Advance lifecycle to award stage
                            if (lifecycle?.id) {
                              await api.post<any>(`/lifecycle/${lifecycle.id}/advance`, { stage: 'award' });
                            }
                            setMessage(`${match.vendor_name} selected as awardee!`);
                            loadOpportunity();
                          } catch (err: any) { setError(err.message); }
                        }}
                        style={{ padding: '8px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      >
                        🏆 Select as Awardee
                      </button>
                      <button
                        onClick={() => handleVendorAnalyze(vendorId, match.vendor_name)}
                        disabled={isAnalyzing}
                        style={{
                          padding: '8px 14px',
                          background: isAnalyzing ? '#93c5fd' : '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: isAnalyzing ? 'default' : 'pointer',
                        }}
                      >
                        {isAnalyzing ? '⏳ Analyzing...' : '🧠 Run AI Analysis'}
                      </button>
                    </div>
                  </div>

                  <div style={{ padding: 20 }}>
                    {/* Upload and Notes Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                      {/* File Upload */}
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Upload Documents</div>
                        <label style={{ display: 'block' }}>
                          <input
                            type="file"
                            accept=".pdf,.docx,.doc,.txt,.xlsx,.csv"
                            onChange={(e) => handleVendorFileUpload(vendorId, match.vendor_name, e)}
                            disabled={isUploading}
                            style={{ display: 'none' }}
                          />
                          <div style={{
                            padding: '24px 16px',
                            border: '2px dashed #d1d5db',
                            borderRadius: 6,
                            textAlign: 'center',
                            cursor: isUploading ? 'default' : 'pointer',
                            background: isUploading ? '#f3f4f6' : '#fafafa',
                            transition: 'border-color 0.2s',
                          }}>
                            <div style={{ fontSize: 20, marginBottom: 4 }}>{isUploading ? '⏳' : '📄'}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                              {isUploading ? 'Uploading...' : 'Click to upload proposal, capability statement, or other files'}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>PDF, DOCX, TXT, XLSX, CSV</div>
                          </div>
                        </label>

                        {/* Uploaded files list */}
                        {evalData?.documents?.length > 0 && (
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {evalData.documents.map((doc: any) => (
                              <div key={doc.document_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f0fdf4', borderRadius: 4, fontSize: 12 }}>
                                <div>
                                  <span style={{ color: '#166534', fontWeight: 500 }}>{doc.filename || 'Notes'}</span>
                                  {doc.extraction_status && (
                                    <span style={{ marginLeft: 8, color: doc.extraction_status === 'completed' ? '#10b981' : '#f59e0b', fontSize: 11 }}>
                                      ({doc.extraction_status})
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteVendorDoc(vendorId, doc.document_id)}
                                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}
                                  title="Remove"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Evaluation Notes</div>
                        <textarea
                          value={vendorNotes[vendorId] || ''}
                          onChange={(e) => setVendorNotes(prev => ({ ...prev, [vendorId]: e.target.value }))}
                          placeholder="Add notes about this vendor's capabilities, past performance, or other evaluation criteria..."
                          style={{
                            width: '100%',
                            minHeight: 100,
                            padding: 12,
                            border: '1px solid #d1d5db',
                            borderRadius: 6,
                            fontSize: 13,
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                          }}
                        />
                        <button
                          onClick={() => handleVendorNotesSubmit(vendorId, match.vendor_name)}
                          disabled={!vendorNotes[vendorId]?.trim() || isUploading}
                          style={{
                            marginTop: 8,
                            padding: '6px 12px',
                            background: !vendorNotes[vendorId]?.trim() ? '#e5e7eb' : '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: !vendorNotes[vendorId]?.trim() ? 'default' : 'pointer',
                          }}
                        >
                          Save Notes
                        </button>
                      </div>
                    </div>

                    {/* AI Evaluation Results */}
                    {evaluation?.output && (
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>
                          🧠 AI Evaluation Results
                          {evaluation.fit_score && (
                            <span style={{ fontWeight: 400, fontSize: 11, color: '#6b7280', marginLeft: 8 }}>
                              FIT: {evaluation.fit_score}/100
                            </span>
                          )}
                        </div>

                        {/* Overall Assessment */}
                        <div style={{ background: '#f8fafc', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Overall Assessment</div>
                          <div style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.5 }}>{evaluation.output.overall_assessment}</div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                          {/* Strengths */}
                          {evaluation.output.strengths?.length > 0 && (
                            <div style={{ background: '#f0fdf4', borderRadius: 6, padding: 12, border: '1px solid #bbf7d0' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#166534', marginBottom: 6 }}>STRENGTHS</div>
                              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#15803d' }}>
                                {evaluation.output.strengths.map((s: string, i: number) => (
                                  <li key={i} style={{ marginBottom: 3 }}>{s}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Weaknesses */}
                          {evaluation.output.weaknesses?.length > 0 && (
                            <div style={{ background: '#fef2f2', borderRadius: 6, padding: 12, border: '1px solid #fecaca' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#991b1b', marginBottom: 6 }}>WEAKNESSES</div>
                              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#dc2626' }}>
                                {evaluation.output.weaknesses.map((w: string, i: number) => (
                                  <li key={i} style={{ marginBottom: 3 }}>{w}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* Requirements Coverage */}
                        {evaluation.output.requirements_coverage && (
                          <div style={{ background: '#f8fafc', borderRadius: 6, padding: 12, marginBottom: 12, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Requirements Coverage</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                              <div>
                                <div style={{ fontSize: 11, color: '#10b981', fontWeight: 600, marginBottom: 4 }}>
                                  Fully Met ({evaluation.output.requirements_coverage.fully_met?.length || 0})
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: '#374151' }}>
                                  {evaluation.output.requirements_coverage.fully_met?.slice(0, 5).map((r: string, i: number) => (
                                    <li key={i} style={{ marginBottom: 2 }}>{r}</li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginBottom: 4 }}>
                                  Partially Met ({evaluation.output.requirements_coverage.partially_met?.length || 0})
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: '#374151' }}>
                                  {evaluation.output.requirements_coverage.partially_met?.slice(0, 5).map((r: string, i: number) => (
                                    <li key={i} style={{ marginBottom: 2 }}>{r}</li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, marginBottom: 4 }}>
                                  Not Addressed ({evaluation.output.requirements_coverage.not_addressed?.length || 0})
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: '#374151' }}>
                                  {evaluation.output.requirements_coverage.not_addressed?.slice(0, 5).map((r: string, i: number) => (
                                    <li key={i} style={{ marginBottom: 2 }}>{r}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Risk Factors & Suggested Questions */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          {evaluation.output.risk_factors?.length > 0 && (
                            <div style={{ background: '#fffbeb', borderRadius: 6, padding: 12, border: '1px solid #fde68a' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>RISK FACTORS</div>
                              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#78350f' }}>
                                {evaluation.output.risk_factors.map((r: string, i: number) => (
                                  <li key={i} style={{ marginBottom: 3 }}>{r}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {evaluation.output.suggested_questions?.length > 0 && (
                            <div style={{ background: '#eff6ff', borderRadius: 6, padding: 12, border: '1px solid #bfdbfe' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#1e40af', marginBottom: 6 }}>SUGGESTED QUESTIONS</div>
                              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#1e3a8a' }}>
                                {evaluation.output.suggested_questions.map((q: string, i: number) => (
                                  <li key={i} style={{ marginBottom: 3 }}>{q}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* Recommendation Rationale */}
                        {evaluation.output.recommendation_rationale && (
                          <div style={{ marginTop: 12, padding: 12, background: '#f1f5f9', borderRadius: 6, border: '1px solid #cbd5e1' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>RECOMMENDATION RATIONALE</div>
                            <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.5 }}>{evaluation.output.recommendation_rationale}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLAUSE SUGGESTER — advisory AI suggestion of applicable FAR + supplement clauses
// ═══════════════════════════════════════════════════════════════════════════════

function ClauseSuggester({ oppId }: { oppId: string }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setLoading(true); setErr(''); setRes(null);
    try {
      const r = await opportunitiesApi.suggestClauses(oppId);
      setRes(r);
    } catch (e: any) {
      setErr(e?.message || 'Could not generate clause suggestions.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', margin: 0 }}>
            <span aria-hidden="true">📎 </span>Suggested Clauses (AI, advisory)
          </h3>
          <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>
            AI proposes likely FAR + agency-supplement provisions/clauses for this opportunity, grounded in its value, commerciality, codes, set-aside, and agency. Verify each against its prescription before use.
          </p>
        </div>
        <button onClick={run} disabled={loading}
          style={{ padding: '9px 16px', background: loading ? '#a5b4fc' : '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
          {loading ? 'Analyzing…' : 'Suggest clauses'}
        </button>
      </div>

      {err && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{err}</div>}

      {res && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {res.organization && <span style={{ padding: '2px 10px', background: '#eef2ff', color: '#4338ca', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>{res.organization}</span>}
            {res.threshold_category && <span style={{ padding: '2px 10px', background: '#e0e7ff', color: '#4338ca', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>{res.threshold_category}</span>}
          </div>
          {res.stack && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>Regulatory stack: {res.stack}</div>}
          {res.clauses_text ? (
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 12.5, color: '#374151', lineHeight: 1.6, background: '#f9fafb', border: '1px solid #f1f5f9', borderRadius: 6, padding: 14, maxHeight: 460, overflowY: 'auto' }}>
              {res.clauses_text}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px' }}>{res.note || 'No suggestions returned.'}</div>
          )}
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8 }}>
            ⚠ {res.note || 'AI-suggested, advisory only. Not an authoritative clause matrix.'}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DRAFT DOCUMENT GENERATOR TAB
// ═══════════════════════════════════════════════════════════════════════════════

function DraftGeneratorTab({ opp, oppId, analysis, documents = [], onSaved, stages, heading }: { opp: any; oppId?: string; analysis: any; documents?: any[]; onSaved?: (drafts: any) => void; stages?: string[]; heading?: string }) {
  // Load previously-saved drafts from the opportunity so they survive reloads.
  const savedDrafts = (() => {
    try { const raw = opp?.solicitation_drafts; return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {}; } catch { return {}; }
  })();
  const { toast } = useToast();
  const [docType, setDocType] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  // Remember the most recently generated Sources Sought so the RFQ can be built
  // from the actual RFI. Seed from any saved draft.
  const [savedSourcesSought, setSavedSourcesSought] = useState(savedDrafts.sources_sought || '');
  // Full FAR-lifecycle document catalog, fetched from the backend (single source
  // of truth: each doc maps to its governing FAR part + lifecycle stage).
  const [catalog, setCatalog] = useState<any[]>([]);

  useEffect(() => {
    let alive = true;
    api.get<any>('/ai/document-catalog')
      .then(res => { if (alive) setCatalog(res.documents || []); })
      .catch(() => { /* picker falls back to empty; user can retry */ });
    return () => { alive = false; };
  }, []);

  // When a `stages` scope is supplied, show only that stage's documents (Option B:
  // each lifecycle tab surfaces the documents that belong to it). No scope = all.
  const visibleCatalog = (stages && stages.length)
    ? catalog.filter((d: any) => stages.includes(d.stage))
    : catalog;

  const selectedDoc = catalog.find((d: any) => d.id === docType);

  // When switching doc type, show the saved draft for that type if present.
  useEffect(() => {
    setDraft(savedDrafts[docType] || '');
  }, [docType]);

  // Single "Save" action: persists the draft as a downloadable, versioned document
  // in the Documents panel AND keeps an inline copy on the opportunity (so the draft
  // reloads on its tab and continues to feed AI chaining). One button, no regression.
  const [savingDoc, setSavingDoc] = useState(false);
  // Default to Word (docx) so the generated document is immediately editable.
  const [docFormat, setDocFormat] = useState<'pdf' | 'docx' | 'txt'>('docx');
  const saveAll = async () => {
    if (!oppId || !draft || !docType) return;
    setSavingDoc(true); setSavedMsg(''); setError('');
    try {
      const title = selectedDoc?.label || docType;
      const far = selectedDoc ? `FAR ${selectedDoc.far_part} (${selectedDoc.cite})` : '';
      // 1) Real downloadable document (versioned, replaces same-type current file).
      const res = await documentsApi.saveDraft(oppId, docType, title, draft, docFormat, {
        opportunity: opp?.title || '',
        procurement_case: opp?.procurement_case_id || '',
        agency: opp?.agency || opp?.department || '',
        far,
      });
      // 2) Keep the inline copy so the draft reloads on its tab + feeds chaining.
      try {
        const next = { ...savedDrafts, [docType]: draft, updated_at: Date.now() };
        await opportunitiesApi.update(oppId, { solicitation_drafts: JSON.stringify(next) });
        onSaved?.(next);
      } catch { /* inline copy is best-effort; the document file is the source of truth */ }
      setSavedMsg(`✓ Saved as a downloadable ${docFormat.toUpperCase()} file in the Documents panel.`);
      toast(`Saved to the Documents panel as a ${docFormat.toUpperCase()} file.`, 'success');
      if (res?.download_url) {
        try { window.open(res.download_url, '_blank'); } catch { /* popup blocked; it's still in Documents */ }
      }
    } catch (e: any) {
      setError(e.message || 'Could not save the document.');
      toast(e.message || 'Could not save the document.', 'error');
    }
    setSavingDoc(false);
  };

  const handleGenerate = async () => {
    if (!docType) return;
    setGenerating(true);
    setDraft('');
    setError('');

    // Build context from opportunity data
    const context = {
      title: opp.title || '',
      agency: opp.agency || '',
      office: opp.office || '',
      notice_id: opp.notice_id || '',
      naics_code: opp.naics_code || '',
      psc_code: opp.psc_code || opp.product_service_code || '',
      set_aside: opp.set_aside || '',
      response_deadline: opp.response_deadline || opp.date_offers_due || '',
      description: opp.description || '',
      budget: opp.budget || opp.budget_amount || '',
      place_of_performance: opp.place_of_performance || '',
      notes: opp.notes || '',
    };

    const requirements = analysis?.output?.requirements || analysis?.requirements || [];
    const reqText = requirements.map((r: any) => typeof r === 'string' ? r : r.text || r.requirement || '').join('\n- ');

    // Pull the actual text of the opportunity's documents (uploaded AND AI-generated
    // saved-as-document) from the backend context endpoint. The plain documents list
    // strips extracted_text for size, so we fetch the real content here so new drafts
    // are grounded in the documents already created/uploaded for this opportunity.
    let sourceDocsText = '';
    if (oppId) {
      try {
        const ctxRes = await documentsApi.context(oppId, 24000);
        sourceDocsText = ctxRes?.context || '';
      } catch { /* non-fatal; fall back to no document text */ }
    }

    // Build a readable summary of the FAR Part 11 Intake data so drafts reflect
    // the mission need, requirements, and conditions captured during intake.
    let intakeText = '';
    try {
      const intake = opp.intake_data ? (typeof opp.intake_data === 'string' ? JSON.parse(opp.intake_data) : opp.intake_data) : null;
      if (intake && Object.keys(intake).length) {
        const lines: string[] = [];
        const push = (lbl: string, v: any) => { if (v && String(v).trim()) lines.push(`${lbl}: ${Array.isArray(v) ? v.filter(Boolean).join('; ') : v}`); };
        push('Mission Need', intake.mission_need);
        push('Intended Outcome', intake.intended_outcome);
        push('Functions to Be Performed', intake.functions_performed);
        push('Intended Users', intake.intended_users);
        push('Operating Environment', intake.operating_environment);
        push('Market Research Findings', intake.mr_findings);
        push('Commercial Solution Determination', intake.commercial_determination);
        push('Requirements Document Type', intake.req_doc_type);
        push('Performance-Based', intake.performance_based);
        // Requirements register
        if (Array.isArray(intake.requirements) && intake.requirements.length) {
          const reqs = intake.requirements.slice(0, 40).map((r: any, i: number) =>
            `  ${r.req_id || 'R-' + (i + 1)} [${r.level || ''}/${r.type || ''}] ${r.title || ''}: ${r.description || ''}`
            + (r.metric ? ` | Metric: ${r.metric}` : '')
            + (r.acceptance ? ` | Acceptance: ${r.acceptance}` : '')
          ).join('\n');
          lines.push('Requirements Register:\n' + reqs);
        }
        push('Promotes Full & Open Competition', intake.promotes_competition);
        push('Period of Performance', [intake.pop_start, intake.pop_end].filter(Boolean).join(' to '));
        if (Array.isArray(intake.milestones) && intake.milestones.length) {
          lines.push('Milestones: ' + intake.milestones.map((m: any) => `${m.milestone || ''} (${m.due || ''})`).filter(Boolean).join('; '));
        }
        push('Section 508 / ICT', intake.is_ict);
        push('Sustainability Applies', intake.sustainability_applies);
        if (lines.length) intakeText = lines.join('\n');
      }
    } catch { /* ignore malformed intake */ }

    // Summarize the Acquisition Method data (commerciality, selected FAR path, and
    // the key fields from the applicable FAR 12/13/14/15 form) so drafts reflect
    // the chosen acquisition strategy.
    let acquisitionText = '';
    try {
      const acq = opp.acquisition_data ? (typeof opp.acquisition_data === 'string' ? JSON.parse(opp.acquisition_data) : opp.acquisition_data) : null;
      if (acq && Object.keys(acq).length) {
        const a: string[] = [];
        const p = (lbl: string, v: any) => { if (v && String(v).trim()) a.push(`${lbl}: ${Array.isArray(v) ? v.filter(Boolean).join('; ') : v}`); };
        p('Commercial?', acq.is_commercial);
        p('Commercial Classification', acq.commercial_classification);
        p('Estimated Value', acq.estimated_value);
        p('Recommended Method', acq.recommended_method);
        p('CO-Selected Method', acq.co_selected_method);
        p('Selection Rationale', acq.selection_rationale);
        const f12 = acq.far12 || {};
        if (Object.keys(f12).length) {
          p('FAR12 Commercial Type', f12.cp_type);
          p('FAR12 Evaluation Method', f12.ev_method);
          p('FAR12 Contract Type', f12.ct_type);
        }
        const f13 = acq.far13 || {};
        if (Object.keys(f13).length) {
          p('FAR13 Set-Aside', f13.sb_decision);
          p('FAR13 Solicitation Method', f13.sb_solicitation_method);
          p('FAR13 Evaluation Method', f13.qe_method);
        }
        const f14 = acq.far14 || {};
        if (Object.keys(f14).length) {
          p('FAR14 Method', f14.method);
          p('FAR14 Contract Type', f14.ifb_contract_type);
        }
        const f15 = acq.far15 || {};
        if (Object.keys(f15).length) {
          p('FAR15 Source-Selection Method', f15.source_selection_method);
          p('FAR15 Discussions', f15.will_discussions);
          p('FAR15 Price/Non-Price Relationship', f15.rfp_price_nonprice);
        }
        if (a.length) acquisitionText = a.join('\n');
      }
    } catch { /* ignore malformed acquisition */ }

    // Merge acquisition into the intake context block so both prompts pick it up.
    if (acquisitionText) {
      intakeText = (intakeText ? intakeText + '\n' : '') + 'ACQUISITION METHOD:\n' + acquisitionText;
    }

    // Previously-created documents for THIS opportunity (the drafts already
    // generated and saved). Every new document builds on the ones before it —
    // e.g. a J&A reflects the Acquisition Plan, an RFQ develops the SOW/PWS, a
    // CPARS references the award. Excludes the doc currently being generated.
    let createdDocsText = '';
    try {
      const labelFor = (k: string) => (catalog.find((d: any) => d.id === k)?.label || k);
      const priorParts: string[] = [];
      let cbudget = 12000;
      for (const [k, v] of Object.entries(savedDrafts)) {
        if (k === docType || k === 'updated_at' || typeof v !== 'string' || !v.trim()) continue;
        const slice = String(v).slice(0, Math.min(5000, cbudget));
        if (!slice) continue;
        priorParts.push(`--- ${labelFor(k)} (previously generated for this opportunity) ---\n${slice}`);
        cbudget -= slice.length;
        if (cbudget <= 0) break;
      }
      createdDocsText = priorParts.join('\n\n');
    } catch { /* ignore */ }

    // Flattened context reused by the catalog document generator (all doc types
    // except the two tuned paths below). Includes opportunity, intake, acquisition,
    // requirements, uploaded document text, AND previously-created documents.
    const flatContext = `OPPORTUNITY DATA:
Title: ${context.title}
Agency: ${context.agency}
Office: ${context.office}
Notice ID: ${context.notice_id || '[TBD]'}
NAICS: ${context.naics_code}
PSC: ${context.psc_code}
Set-Aside: ${context.set_aside}
Response Deadline: ${context.response_deadline}
Budget: ${context.budget}
Place of Performance: ${context.place_of_performance}
Description: ${context.description}
Notes: ${context.notes || 'None'}

EXTRACTED REQUIREMENTS:
- ${reqText || 'None extracted yet'}
${intakeText ? `\nINTAKE / ACQUISITION (FAR Part 11 + method):\n${intakeText}` : ''}
${sourceDocsText ? `\nUPLOADED DOCUMENTS:\n${sourceDocsText.slice(0, 10000)}` : ''}
${createdDocsText ? `\nPREVIOUSLY-CREATED DOCUMENTS FOR THIS OPPORTUNITY (build on these; stay consistent with them):\n${createdDocsText}` : ''}`;

    const orgLabel = (opp.contracting_organization || opp.agency || '').trim();

    // Documents OTHER than the two tuned UCF paths (sources_sought, rfq/rfp_ucf)
    // go through the catalog generator: one grounded call per document.
    if (docType && docType !== 'sources_sought' && docType !== 'rfq' && docType !== 'rfp_ucf') {
      try {
        const res = await api.post<any>('/ai/generate-document', {
          document_type: docType,
          organization: orgLabel,
          context: flatContext,
        });
        setDraft(res.answer || res.response || res.message || '');
      } catch (err: any) {
        setError(err.message || 'Generation failed');
      }
      setGenerating(false);
      return;
    }

    let prompt = '';
    if (docType === 'sources_sought') {
      prompt = `Generate a complete Sources Sought notice draft for this federal procurement. Use the following format with ALL sections:

FORMAT:
1. NOTICE INFORMATION: Notice ID, agency, office, NAICS, PSC, response deadline
2. PURPOSE: Why the government is conducting market research (2-3 sentences)
3. DISCLAIMER: Standard disclaimer that this is not an RFP/RFQ, no award will be made, no reimbursement for response costs
4. BACKGROUND: Mission context, program background, current environment (1-2 paragraphs)
5. DRAFT REQUIREMENT: Objectives, scope, tasks, desired outcomes (detailed, 3-5 paragraphs)
6. ANTICIPATED ACQUISITION: Possible contract type, period of performance, vehicle, estimated size
7. MARKET RESEARCH QUESTIONS: 8-12 specific questions vendors must answer (numbered)
8. BUSINESS INFORMATION REQUESTED: Size status, socioeconomic category, UEI, CAGE, contract vehicles held
9. SUBMISSION INSTRUCTIONS: Page limit, file format, email/contact, deadline, and marking requirements
10. ATTACHMENTS: List any draft PWS/SOW, technical requirements, or templates to be attached

OPPORTUNITY DATA:
Title: ${context.title}
Agency: ${context.agency}
Office: ${context.office}
NAICS: ${context.naics_code}
PSC: ${context.psc_code}
Set-Aside: ${context.set_aside}
Response Deadline: ${context.response_deadline}
Budget: ${context.budget}
Place of Performance: ${context.place_of_performance}
Description: ${context.description}

EXTRACTED REQUIREMENTS:
- ${reqText || 'None extracted yet'}

ADDITIONAL NOTES:
${context.notes || 'None'}

${intakeText ? `INTAKE (FAR Part 11 — mission need, requirements, conditions captured for this opportunity):\n${intakeText}\n` : ''}
${sourceDocsText ? `OPPORTUNITY DOCUMENTS (uploaded to this opportunity — use as source material):\n${sourceDocsText}\n` : ''}
IMPORTANT GROUNDING RULES:
- Base the draft on the OPPORTUNITY DATA, INTAKE, and OPPORTUNITY DOCUMENTS above. The Intake mission need and requirements register are the primary source of the requirement — develop the Sources Sought from them.
- Do NOT fabricate a solicitation/notice number, agency, dates, dollar values, POC names, or FAR/DFARS/HSAR clause numbers. Use only values present above; where a value is unknown, write "[TBD]".
- If the documents indicate a non-standard vehicle (e.g., Commercial Solutions Opening/CSO, OTA, BAA), follow THAT structure and authority rather than a generic format.

Generate a professional, complete Sources Sought notice ready for publication. Use formal government language. Be specific and detailed, but only using the information provided.`;
    } else {
      prompt = `You are drafting the next-stage solicitation (a Request for Quote / RFQ) that FOLLOWS the Request for Information (RFI) / market-research notice described below. Take the scope, requirements, background, and vendor responses from the RFI content and DEVELOP them into a complete, ready-to-review RFQ solicitation organized according to the Uniform Contract Format (UCF).

CRITICAL DEPTH REQUIREMENTS:
- Every section must be FULLY WRITTEN with substantive, specific content — multiple sentences or paragraphs, or concrete itemized lists. Do NOT write placeholder phrases like "will be determined based on the proposed solutions" or "to be determined by the Government." If a fact is genuinely unknown, make a reasonable, clearly-reasonable draft assumption and mark it "[DRAFT ASSUMPTION – confirm]" so a contracting officer can verify it.
- Carry the RFI's scope and requirements forward: convert the RFI's draft requirement / objectives into a real Statement of Work with numbered tasks, deliverables, and measurable performance standards.
- Section B: propose an actual CLIN structure (e.g., CLIN 0001 base, option CLINs) with descriptions, quantity, unit, and pricing arrangement (FFP/T&M) appropriate to the requirement.
- Section M: define real evaluation factors with relative weighting and a rating method consistent with the acquisition approach in the source documents.

FORMAT (UCF) — populate ALL sections in depth:
PART I – SCHEDULE
  Section A: Solicitation/Contract Form (solicitation number, issuing office, key dates, type of solicitation)
  Section B: Supplies or Services and Pricing (full CLIN structure with descriptions, quantities, units, pricing arrangement)
  Section C: Statement of Work / Performance Work Statement (detailed scope, numbered tasks, deliverables, performance standards, period)
  Section D: Packaging and Marking
  Section E: Inspection and Acceptance (criteria, procedures, acceptance authority)
  Section F: Deliveries or Performance (period of performance, delivery/milestone schedule, place of performance)
  Section G: Contract Administration (COR designation, invoicing instructions, reporting)
  Section H: Special Contract Requirements (security/suitability, key personnel, OCI, transition, data rights)

PART II – CLAUSES
  Section I: Contract Clauses (list applicable FAR/DFARS/HSAR clauses BY NUMBER AND TITLE that actually fit this requirement and appear in or are implied by the source; do not invent unrelated clauses)

PART III – ATTACHMENTS
  Section J: List of Attachments (SOW/PWS, pricing template, past performance questionnaire, technical exhibits)

PART IV – OFFEROR INFORMATION
  Section K: Representations and Certifications (the specific reps/certs required)
  Section L: Instructions, Conditions, and Notices to Offerors (quote format, volumes, page limits, submission method, due date)
  Section M: Evaluation Factors (factors, subfactors, relative importance/weighting, rating methodology, basis for award)

OPPORTUNITY DATA:
Title: ${context.title}
Agency: ${context.agency}
Office: ${context.office}
NAICS: ${context.naics_code}
PSC: ${context.psc_code}
Set-Aside: ${context.set_aside}
Response Deadline: ${context.response_deadline}
Budget: ${context.budget}
Place of Performance: ${context.place_of_performance}
Description: ${context.description}

EXTRACTED REQUIREMENTS:
- ${reqText || 'None extracted yet'}

ADDITIONAL NOTES:
${context.notes || 'None'}

${sourceDocsText ? `SOURCE NOTICE DOCUMENTS (the actual uploaded notice — base the draft on THIS content):\n${sourceDocsText}\n` : ''}
IMPORTANT GROUNDING RULES:
- Base the draft on the OPPORTUNITY DATA and SOURCE NOTICE DOCUMENTS above. Do not invent facts.
- Do NOT fabricate a solicitation number, dates, dollar values, POC names, or clause numbers. Use only values present above; where a value is unknown, write "[TBD]" rather than guessing.
- If the source documents indicate a non-FAR vehicle (e.g., Commercial Solutions Opening/CSO under NDAA 880, OTA, or BAA), follow THAT document's structure, authority, phases, and submission process instead of forcing the standard UCF A–M layout.
- Only cite FAR/DFARS/HSAR clauses that actually fit this requirement; do not add generic clauses that are not relevant.

This is the RFQ that comes AFTER the RFI — develop the RFI's market-research content into a complete, biddable solicitation. Populate EVERY section with real, specific draft content (no "to be determined" filler). Use formal government contracting language, grounded in the information provided, marking any drafting assumptions clearly.`;
    }

    // Sources Sought is short enough for a single call.
    if (docType === 'sources_sought') {
      try {
        const res = await api.post<any>('/ai/ask', { question: prompt, context: 'draft_generation', target_model: 'sources_sought' });
        const answer = res.answer || res.response || res.message || '';
        setDraft(answer);
        // Remember it so the RFQ can be developed from this actual RFI, and persist
        // it to the opportunity so the RFQ generator on the Solicitation Development
        // tab can find it even across tab switches / reloads.
        if (answer) {
          setSavedSourcesSought(answer);
          if (oppId) {
            const next = { ...savedDrafts, sources_sought: answer, updated_at: Date.now() };
            opportunitiesApi.update(oppId, { solicitation_drafts: JSON.stringify(next) })
              .then(() => onSaved?.(next))
              .catch(() => { /* non-fatal; user can still Save manually */ });
          }
        }
      } catch (err: any) {
        setError(err.message || 'Generation failed');
      }
      setGenerating(false);
      return;
    }

    // RFQ / full UCF solicitation: generate section-by-section so each call is
    // small and finishes well under the API Gateway 29s limit. A single call for
    // the whole A–M document times out ("Failed to fetch").
    // Keep a compact shared context so each per-section prompt stays lean.
    const compactSource = sourceDocsText ? sourceDocsText.slice(0, 8000) : '';
    // Prefer the actual generated Sources Sought (the RFI) as the basis for the RFQ.
    const rfiBasis = (savedSourcesSought || '').slice(0, 9000);
    const compactIntake = intakeText ? intakeText.slice(0, 6000) : '';
    const sharedContext = `OPPORTUNITY DATA:
Title: ${context.title}
Agency: ${context.agency}
Office: ${context.office}
Notice ID: ${context.notice_id || '[TBD]'}
NAICS: ${context.naics_code}
PSC: ${context.psc_code}
Set-Aside: ${context.set_aside}
Response Deadline: ${context.response_deadline}
Budget: ${context.budget}
Place of Performance: ${context.place_of_performance}
Description: ${context.description}

EXTRACTED REQUIREMENTS:
- ${reqText || 'None extracted yet'}

ADDITIONAL NOTES:
${context.notes || 'None'}
${compactIntake ? `\nINTAKE (FAR Part 11 — mission need and requirements register):\n${compactIntake}` : ''}
${rfiBasis ? `\nSOURCES SOUGHT / RFI (the actual RFI generated for this opportunity — DEVELOP THE RFQ FROM THIS):\n${rfiBasis}` : ''}
${!rfiBasis && compactSource ? `\nOPPORTUNITY DOCUMENTS (uploaded — use as source material):\n${compactSource}` : ''}
${createdDocsText ? `\nOTHER PREVIOUSLY-CREATED DOCUMENTS FOR THIS OPPORTUNITY (stay consistent with these):\n${createdDocsText.slice(0, 8000)}` : ''}

GROUNDING RULES: Develop the RFQ from the SOURCES SOUGHT / RFI and INTAKE above. Do not fabricate solicitation numbers, dates, dollar values, POC names, or clause numbers; use provided values or "[TBD]". Populate content in depth — no "to be determined" filler; mark reasonable draft assumptions as "[DRAFT ASSUMPTION - confirm]". No markdown symbols.`;

    const sectionGroups: { heading: string; instruction: string }[] = [
      {
        heading: 'PART I - SCHEDULE (Sections A, B)',
        instruction: 'Write Section A (Solicitation/Contract Form: solicitation number, issuing office, key dates, type) and Section B (Supplies or Services and Pricing: a full CLIN structure — base CLIN and any option CLINs, each with description, quantity, unit, and pricing arrangement such as FFP or T&M).',
      },
      {
        heading: 'PART I - SCHEDULE (Section C - Statement of Work)',
        instruction: 'Write Section C: a detailed Statement of Work / Performance Work Statement developed from the RFI requirement. Include background, scope, numbered tasks, specific deliverables, and measurable performance standards.',
      },
      {
        heading: 'PART I - SCHEDULE (Sections D, E, F, G, H)',
        instruction: 'Write Section D (Packaging and Marking), Section E (Inspection and Acceptance: criteria, procedures, acceptance authority), Section F (Deliveries or Performance: period of performance, milestone/delivery schedule, place of performance), Section G (Contract Administration: COR, invoicing instructions, reporting), and Section H (Special Contract Requirements: security/suitability, key personnel, OCI, transition, data rights). Populate each with specific content.',
      },
      {
        heading: 'PART II & III - CLAUSES AND ATTACHMENTS (Sections I, J)',
        instruction: 'Write Section I (Contract Clauses: list applicable FAR/DFARS/HSAR clauses BY NUMBER AND TITLE that genuinely fit this requirement) and Section J (List of Attachments: SOW/PWS, pricing template, past performance questionnaire, technical exhibits).',
      },
      {
        heading: 'PART IV - OFFEROR INFORMATION (Sections K, L, M)',
        instruction: 'Write Section K (Representations and Certifications required), Section L (Instructions, Conditions, and Notices to Offerors: quote format, volumes, page limits, submission method, due date), and Section M (Evaluation Factors: factors, subfactors, relative importance/weighting, rating methodology, basis for award).',
      },
    ];

    try {
      const pieces: string[] = ['SOLICITATION (RFQ) - UNIFORM CONTRACT FORMAT\nDeveloped from the RFI / market research notice.\n'];
      setDraft(pieces.join('\n'));
      for (const grp of sectionGroups) {
        const sectionPrompt = `You are drafting one part of a Request for Quote (RFQ) solicitation in Uniform Contract Format, developed from the RFI below. Write ONLY the requested section(s) now, fully and in depth.

${grp.instruction}

${sharedContext}

Output only the section(s) requested, with clear section headings. Be specific and substantive.`;
        try {
          const res = await api.post<any>('/ai/ask', { question: sectionPrompt, context: 'draft_generation', target_model: 'rfq' });
          const answer = (res.answer || res.response || res.message || '').trim();
          pieces.push(`\n${grp.heading}\n${'='.repeat(60)}\n${answer}`);
        } catch (secErr: any) {
          pieces.push(`\n${grp.heading}\n${'='.repeat(60)}\n[Section generation failed: ${secErr.message || 'error'} — you can retry.]`);
        }
        setDraft(pieces.join('\n'));
      }
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    }
    setGenerating(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{heading || 'Document Draft Generator'}</h3>
      </div>

      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        Generate a draft document using AI based on the opportunity data and extracted requirements. Each document is grounded in its governing FAR part plus this organization&apos;s supplement. Choose a document below.
      </p>

      {/* Document Type Selection — FAR-lifecycle catalog, grouped by stage. */}
      {catalog.length === 0 ? (
        <div style={{ padding: 14, color: '#9ca3af', fontSize: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 20 }}>
          Loading document catalog…
        </div>
      ) : (
        <div style={{ marginBottom: 20 }}>
          {(() => {
            // Group by stage_label, preserving the backend's lifecycle order.
            const groups: { label: string; docs: any[] }[] = [];
            for (const d of visibleCatalog) {
              let g = groups.find(x => x.label === d.stage_label);
              if (!g) { g = { label: d.stage_label, docs: [] }; groups.push(g); }
              g.docs.push(d);
            }
            if (groups.length === 0) {
              return <div style={{ padding: 12, color: '#9ca3af', fontSize: 12 }}>No documents for this stage.</div>;
            }
            return groups.map(g => (
              <div key={g.label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{g.label}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
                  {g.docs.map((d: any) => {
                    const active = docType === d.id;
                    const hasSaved = !!savedDrafts[d.id];
                    return (
                      <button
                        key={d.id}
                        onClick={() => setDocType(d.id)}
                        title={d.scope}
                        style={{
                          padding: '12px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                          background: active ? '#ede9fe' : 'white',
                          border: active ? '2px solid #7c3aed' : '1px solid #d1d5db',
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600, color: active ? '#5b21b6' : '#1f2937' }}>
                          {d.label}{hasSaved && <span style={{ color: '#059669', marginLeft: 6 }} title="A saved draft exists">●</span>}
                        </div>
                        <div style={{ fontSize: 10, color: '#7c8db0', marginTop: 4 }}>FAR {d.far_part} · {d.cite}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* Generate Button */}
      {docType && (
        <>
          {(docType === 'rfq' || docType === 'rfp_ucf') && (
            <div style={{ fontSize: 11, marginBottom: 8, color: savedSourcesSought ? '#065f46' : '#b45309' }}>
              {savedSourcesSought ? '✓ Will build from the saved Sources Sought (generated on the Intake tab).' : 'Tip: generate the Sources Sought on the Intake tab first so the solicitation develops from the actual RFI. Otherwise it uses intake + documents.'}
            </div>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              padding: '12px 24px', background: generating ? '#93c5fd' : '#7c3aed', color: 'white',
              border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
              cursor: generating ? 'wait' : 'pointer', marginBottom: 16,
            }}
          >
            {generating ? '⏳ Generating draft…' : `✨ Generate ${selectedDoc?.label || 'Document'} Draft`}
          </button>
        </>
      )}

      {error && <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#991b1b' }}>{error}</div>}

      {/* Draft Output */}
      {draft && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
              {selectedDoc?.label || 'Document'} — DRAFT{selectedDoc ? ` · FAR ${selectedDoc.far_part} (${selectedDoc.cite})` : ''}
            </h4>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { navigator.clipboard.writeText(draft); }}
                style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}
              >
                📋 Copy
              </button>
              {oppId && (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <select
                    value={docFormat}
                    onChange={e => setDocFormat(e.target.value as any)}
                    aria-label="Save format"
                    style={{ padding: '6px 6px', border: '1px solid #d1d5db', borderRight: 'none', borderRadius: '4px 0 0 4px', fontSize: 11, background: 'white' }}
                  >
                    <option value="docx">Word</option>
                    <option value="pdf">PDF</option>
                    <option value="txt">Text</option>
                  </select>
                  <button
                    onClick={saveAll}
                    disabled={savingDoc}
                    title="Save to the Documents panel as a downloadable, editable file"
                    style={{ padding: '6px 14px', background: savingDoc ? '#86efac' : '#10b981', color: 'white', border: 'none', borderRadius: '0 4px 4px 0', fontSize: 11, fontWeight: 600, cursor: savingDoc ? 'default' : 'pointer' }}
                  >
                    {savingDoc ? 'Saving…' : '💾 Save'}
                  </button>
                </span>
              )}
            </div>
          </div>
          {savedMsg && <div style={{ padding: '6px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, fontSize: 11, color: '#166534', marginBottom: 10 }}>{savedMsg}</div>}
          <div style={{ padding: '6px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, fontSize: 11, color: '#92400e', marginBottom: 10 }}>
            AI-generated DRAFT grounded in the governing FAR authority and this organization&apos;s supplement. Requires contracting-officer review before use.
          </div>
          <div style={{
            whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.8, color: '#1f2937',
            maxHeight: 600, overflowY: 'auto', padding: 16, background: '#fafafa',
            borderRadius: 6, border: '1px solid #e5e7eb', fontFamily: 'system-ui',
          }}>
            {draft}
          </div>
        </div>
      )}
    </div>
  );
}
